import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import ClientPortal from "@/ClientPortal";
import PublicBookingWidget from "@/booking/PublicBookingWidget";
import TermsOfService from "@/legal/TermsOfService";
import PrivacyPolicy from "@/legal/PrivacyPolicy";
import IntegrationCenterEnhanced from "@/IntegrationCenterEnhanced";
import { TwoFactorEnrollment, TwoFactorVerification, SecurityDashboard, SessionManagement, AuditLogPanel, PDFDownloadLog } from "@/SecurityComponents";
import SystemHealthDashboard from "@/system/SystemHealthDashboard";
import DemoWorkspaceManager from "@/system/DemoWorkspaceManager";
import LeadDiscoveryDashboard from "@/revenue/LeadDiscoveryDashboard";
import LeadDatabase from "@/revenue/LeadDatabase";
import LeadCommandCenter from "@/revenue/LeadCommandCenter";
import { isModuleAllowed, isNavItemAllowed } from "@/lib/navAccess";
import CSVImportWizard from "@/revenue/CSVImportWizard";
import DuplicateReviewQueue from "@/revenue/DuplicateReviewQueue";
import AILeadScoringDashboard from "@/revenue/AILeadScoringDashboard";
import AILeadIntelligenceDashboard from "@/revenue/AILeadIntelligenceDashboard";
import RevenueDashboard from "@/revenue/RevenueDashboard";
import AIPriorityQueue from "@/revenue/AIPriorityQueue";
import ComplianceCenter from "@/revenue/ComplianceCenter";
import OutreachApprovalQueue from "@/revenue/OutreachApprovalQueue";
import ProviderManagement from "@/revenue/ProviderManagement";
import LeadEnrichmentManagement from "@/revenue/LeadEnrichmentManagement";
import BookingCenter from "@/booking/BookingCenter";
import ClientSuccessCenter from "@/client-success/ClientSuccessCenter";
import { downloadFile } from "@/lib/downloadFile";
import BillingCheckoutLinkPanel from "@/billing/BillingCheckoutLinkPanel";
import ProposalSendAction from "@/proposals/ProposalSendAction";
import UnifiedInbox from "@/inbox/UnifiedInbox";
import GoogleConnectionPanel from "@/integrations/GoogleConnectionPanel";
import AutomationCenter, { TemplateApprovals } from "@/automations/AutomationCenter";
import ColdEmailSequences from "@/campaigns/ColdEmailSequences";
import {
  Activity,
  BadgeDollarSign,
  BarChart3,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  Bot,
  Download,
  FileDown,
  Inbox,
  Mail,
  MapPinned,
  Mic,
  Upload,
  Workflow,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Database,
  FileText,
  Filter,
  Gauge,
  Heart,
  LayoutDashboard,
  Lock,
  LockKeyhole,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Presentation,
  Save,
  Search,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PIPELINE_STAGES = ["Lead Found", "Contacted", "Interested", "Audit Booked", "Audit Delivered", "Proposal Sent", "Negotiation", "Won", "Lost"];
const MODULE_TITLES = {
  dashboard: "CEO Command Center",
  leads: "Lead Database",
  pipeline: "Sales Pipeline",
  team: "Team Management",
  tasks: "Task Management",
  calendar: "Appointment Calendar",
  clients: "Client Management",
  audits: "Audit Center",
  revenue: "Revenue Dashboard",
  documents: "Document Center",
  sops: "SOP Library",
  activity: "Activity Tracking",
  permissions: "Role & Permission Management",
  lead_acquisition: "Lead Scraper Center",
  cold_email: "Cold Email Center",
  cold_calling: "Cold Calling Center",
  proposal_generator: "Proposal Generator",
  onboarding_center: "Client Onboarding Center",
  integrations: "Integration Center",
  inbox: "Unified Communication Inbox",
  ai_sales: "AI Sales Assistant",
  billing: "Stripe Billing Center",
  system_health: "System Health Center",
  demo_workspace: "Demo Workspace",
  backups: "Backup & Recovery Center",
  notifications: "Notification Center",
  automations: "Workflow Automation Center",
  qa_testing: "QA & Testing Center",
  admin_analytics: "Master Admin Analytics",
  security: "Security & Audit Center",
  client_success: "Client Success Center",
  revenue_engine: "Revenue Engine",
  revenue_engine_discovery: "Lead Discovery",
  revenue_engine_database: "Lead Database",
  revenue_engine_detail: "Lead Detail",
  revenue_engine_import: "CSV Import",
  revenue_engine_duplicates: "Duplicate Queue",
  revenue_engine_scoring: "AI Scoring",
  revenue_engine_intelligence: "AI Intelligence",
  revenue_dashboard: "Revenue Dashboard",
  priority_queue: "AI Priority Queue",
  compliance_center: "Compliance Center",
  outreach_approval: "Outreach Approval",
  automation_approvals: "Automation Message Approvals",
  provider_management: "Provider Management",
  lead_enrichment: "Lead Enrichment",
};

const NAV = [
  { id: "dashboard", label: "CEO Command", icon: LayoutDashboard, group: "Dashboard" },
  { id: "client_success", label: "Client Success", icon: Sparkles, group: "Dashboard" },
  { id: "revenue_dashboard", label: "Revenue Dashboard", icon: BadgeDollarSign, group: "Dashboard" },
  { id: "revenue_engine", label: "Revenue Engine", icon: Sparkles, group: "Acquisition" },
  { id: "priority_queue", label: "AI Priority Queue", icon: TrendingUp, group: "Acquisition" },
  { id: "compliance_center", label: "Compliance Center", icon: ShieldCheck, group: "Acquisition" },
  // Not a permission module of its own: gated by revenue_engine, the same
  // module the backend (outreach_routes.py) requires.
  { id: "outreach_approval", label: "Outreach Approval", icon: Mail, group: "Acquisition", module: "revenue_engine" },
  // Sales Managers approve the email/SMS templates automations may send (backend: automation_engine.py).
  { id: "automation_approvals", label: "Automation Approvals", icon: Zap, group: "Acquisition", module: "revenue_engine", roles: ["Master Administrator", "Sales Manager"] },
  { id: "provider_management", label: "Provider Management", icon: Server, group: "Acquisition" },
  { id: "lead_enrichment", label: "Lead Enrichment", icon: Database, group: "Acquisition" },
  { id: "lead_acquisition", label: "Lead Scraper", icon: MapPinned, group: "Acquisition" },
  { id: "leads", label: "Lead Database", icon: Database, group: "Acquisition" },
  { id: "cold_email", label: "Cold Email", icon: Mail, group: "Acquisition" },
  { id: "cold_calling", label: "Cold Calling", icon: Mic, group: "Acquisition" },
  { id: "inbox", label: "Unified Inbox", icon: Inbox, group: "Acquisition" },
  { id: "pipeline", label: "Sales Pipeline", icon: BarChart3, group: "Sales" },
  { id: "audits", label: "AI Audit Center", icon: Sparkles, group: "Sales" },
  { id: "proposal_generator", label: "Proposals", icon: BriefcaseBusiness, group: "Sales" },
  { id: "ai_sales", label: "AI Sales", icon: Bot, group: "Sales" },
  { id: "onboarding_center", label: "Onboarding", icon: Workflow, group: "Clients" },
  { id: "clients", label: "Clients", icon: Building2, group: "Clients" },
  { id: "tasks", label: "Tasks", icon: ClipboardList, group: "Operations" },
  { id: "calendar", label: "Calendar", icon: CalendarDays, group: "Operations" },
  { id: "team", label: "Team", icon: Users, group: "Team" },
  { id: "revenue", label: "Revenue", icon: BadgeDollarSign, group: "Reports" },
  { id: "activity", label: "Activity Feed", icon: Activity, group: "Reports" },
  { id: "documents", label: "Documents", icon: FileText, group: "Documents" },
  { id: "sops", label: "SOP Library", icon: BookOpen, group: "Documents" },
  { id: "billing", label: "Stripe Billing", icon: BadgeDollarSign, group: "Billing", masterAdminOnly: true },
  { id: "integrations", label: "Integrations", icon: ShieldCheck, group: "Settings" },
  { id: "permissions", label: "Permissions", icon: ShieldCheck, group: "Settings" },
  { id: "account", label: "My Account", icon: LockKeyhole, group: "Settings", everyone: true },
  { id: "system_health", label: "System Health", icon: Heart, group: "Production", masterAdminOnly: true },
  { id: "demo_workspace", label: "Demo Workspace", icon: Presentation, group: "Production", masterAdminOnly: true },
  { id: "backups", label: "Backups", icon: Download, group: "Production", masterAdminOnly: true },
  { id: "notifications", label: "Notifications", icon: Bell, group: "Production", masterAdminOnly: true },
  { id: "automations", label: "Automations", icon: Zap, group: "Production", masterAdminOnly: true },
  { id: "qa_testing", label: "QA & Testing", icon: ShieldAlert, group: "Production", masterAdminOnly: true },
  { id: "admin_analytics", label: "Admin Analytics", icon: TrendingUp, group: "Production", masterAdminOnly: true },
  { id: "security", label: "Security & Audit", icon: Lock, group: "Production", masterAdminOnly: true },
];
const GROUP_ORDER = ["Dashboard", "Acquisition", "Sales", "Clients", "Operations", "Team", "Reports", "Documents", "Billing", "Settings", "Production"];
const COLLECTION_BY_MODULE = { leads: "leads", team: "team", tasks: "tasks", calendar: "appointments", clients: "clients", audits: "audits", revenue: "revenue", documents: "documents", sops: "sops", activity: "activities", cold_email: "cold_email_campaigns", cold_calling: "call_queue", proposal_generator: "generated_proposals", onboarding_center: "onboarding_workflows", integrations: "integration_connections", inbox: "unified_messages", ai_sales: "ai_sales_sessions" };

const PHASE3_COLLECTIONS = ["lead_acquisition", "cold_email", "cold_calling", "proposal_generator", "onboarding_center", "integrations", "inbox", "ai_sales"];

const FIELD_SCHEMAS = {
  leads: [["clinic_name", "Clinic Name"], ["owner_name", "Owner Name"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["google_business_profile", "Google Business Profile"], ["location", "Location"], ["services_offered", "Services Offered", "textarea"], ["reviews_count", "Reviews Count", "number"], ["lead_source", "Lead Source"], ["assigned_team_member_id", "Assigned User", "team"], ["status", "Stage", "stage"], ["deal_value", "Revenue Range / Pipeline Value", "number"], ["proposal_status", "Proposal Status"], ["proposal_value", "Proposal Value", "number"], ["follow_up_status", "Follow Up Status"], ["notes", "Notes", "textarea"], ["last_contact_date", "Last Contact Date", "date"], ["next_follow_up_date", "Next Follow Up Date", "date"]],
  team: [["name", "Name"], ["email", "Email"], ["role", "Role", "role"], ["title", "Title"], ["phone", "Phone"], ["active", "Active", "checkbox"], ["password", "Temporary Password (leave blank to generate one)"]],
  tasks: [["title", "Task Title"], ["description", "Description", "textarea"], ["assignee_id", "Task Owner", "team"], ["priority", "Priority", "priority"], ["status", "Task Status", "taskStatus"], ["due_date", "Due Date", "date"], ["related_to", "Related To"]],
  calendar: [["title", "Appointment Title"], ["type", "Type"], ["date", "Date", "date"], ["time", "Time"], ["attendees", "Attendees"], ["assigned_team_member_id", "Owner", "team"], ["status", "Status"], ["notes", "Notes", "textarea"]],
  clients: [["clinic_name", "Clinic Name"], ["owner_name", "Owner Name"], ["email", "Email"], ["phone", "Phone"], ["setup_fee", "Setup Fee", "number"], ["monthly_retainer", "Monthly Retainer", "number"], ["ad_spend", "Ad Spend", "number"], ["contract_status", "Client Status"], ["start_date", "Start Date", "date"], ["renewal_date", "Renewal Date", "date"], ["account_manager_id", "Client Owner", "team"], ["onboarding_status", "Onboarding Status"], ["health_score", "Health Score", "number"], ["notes", "Notes", "textarea"]],
  audits: [["clinic_name", "Clinic Name"], ["website_url", "Website URL"], ["google_business_url", "Google Business URL"], ["instagram_url", "Instagram URL"], ["status", "Audit Status"], ["delivery_status", "Delivery Status"], ["executive_summary", "Executive Summary", "textarea"], ["revenue_opportunity_estimate", "Revenue Opportunity", "textarea"]],
  revenue: [["client_name", "Client Name"], ["month", "Month"], ["setup_fee", "Setup Fee", "number"], ["retainer", "Retainer", "number"], ["ad_spend", "Ad Spend", "number"], ["upsells", "Upsells", "number"], ["status", "Status"], ["tracking_type", "Tracking Type"], ["forecast_category", "Forecast Category"]],
  documents: [["title", "Document Title"], ["category", "Category"], ["owner", "Owner"], ["url", "Document URL"], ["status", "Status"], ["notes", "Notes", "textarea"]],
  sops: [["title", "SOP Title"], ["department", "Department"], ["version", "Version"], ["owner", "Owner"], ["status", "Status"], ["content", "Content", "textarea"]],
  cold_email: [["name", "Campaign Name"], ["source", "Source"], ["status", "Status"], ["owner_id", "Owner", "team"], ["leads_enrolled", "Leads Enrolled", "number"], ["opens", "Opens", "number"], ["replies", "Replies", "number"], ["follow_ups_due", "Follow-ups Due", "number"]],
  cold_calling: [["name", "Queue Name"], ["status", "Status"], ["owner_id", "Owner", "team"], ["calls_due", "Calls Due", "number"], ["connected", "Connected", "number"], ["appointments_booked", "Appointments Booked", "number"], ["follow_ups_due", "Follow-ups Due", "number"]],
  proposal_generator: [["clinic_name", "Clinic Name"], ["status", "Status"], ["setup_fee", "Setup Fee", "number"], ["monthly_management", "Monthly Management", "number"], ["owner_id", "Owner", "team"]],
  onboarding_center: [["name", "Workflow Name"], ["status", "Status"], ["owner_id", "Owner", "team"]],
  integrations: [["name", "Integration Name"], ["module", "Module"], ["status", "Status"], ["connection_status", "Connection Status"]],
  inbox: [["channel", "Channel"], ["from_name", "From"], ["clinic_name", "Clinic"], ["subject", "Subject"], ["body", "Message", "textarea"], ["status", "Status"], ["owner_id", "Owner", "team"]],
  ai_sales: [["clinic_name", "Clinic Name"], ["goal", "Goal"], ["status", "Status"]],
};
const TABLE_COLUMNS = {
  lead_acquisition: ["name", "source", "import_type", "provider", "status", "records_total", "duplicates"],
  leads: ["clinic_name", "location", "status", "lead_source", "assigned_team_member", "next_follow_up_date", "deal_value"],
  team: ["name", "email", "role", "title", "active"],
  tasks: ["title", "assignee", "priority", "status", "due_date"],
  calendar: ["title", "type", "date", "time", "status"],
  clients: ["clinic_name", "contract_status", "monthly_retainer", "ad_spend", "renewal_date", "account_manager"],
  audits: ["clinic_name", "overall_medspa_growth_score", "status", "delivery_status", "created_by"],
  revenue: ["client_name", "month", "setup_fee", "retainer", "upsells", "status"],
  documents: ["title", "category", "owner", "status", "url"],
  sops: ["title", "department", "version", "owner", "status"],
  activity: ["timestamp", "actor_name", "actor_role", "action", "entity_type", "details"],
  cold_email: ["name", "source", "status", "leads_enrolled", "opens", "replies", "follow_ups_due"],
  cold_calling: ["name", "status", "calls_due", "connected", "appointments_booked", "follow_ups_due"],
  proposal_generator: ["clinic_name", "status", "setup_fee", "monthly_management", "owner_name"],
  onboarding_center: ["name", "status", "owner_name", "milestones"],
  integrations: ["name", "module", "status", "connection_status", "requires_master_admin"],
  inbox: ["channel", "from_name", "clinic_name", "subject", "status", "owner_name"],
  ai_sales: ["clinic_name", "goal", "status", "created_by", "created_at"],
};
const ROLES = ["Master Administrator", "Operations Manager", "Sales Manager", "Sales Representative", "Lead Generation Specialist", "Client Success Manager", "Virtual Assistant"];

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });
const money = (value) => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const labelize = (value) => String(value || "").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
const today = () => new Date().toISOString().slice(0, 10);
const slug = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Notice({ notice, onClose }) {
  if (!notice) return null;
  return <div className={`notice ${notice.type || "info"}`} data-testid="system-notice"><span>{notice.message}</span><button data-testid="system-notice-close-button" onClick={onClose}><X size={16} /></button></div>;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  
  const submit = async (event) => { 
    event.preventDefault(); 
    setLoading(true); 
    setError(""); 
    try { 
      const response = await axios.post(`${API}/auth/login`, { email, password }); 
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
        setLoading(false);
        return;
      }
      onLogin(response.data); 
    } catch (err) { 
      setError(err.response?.data?.detail || "Login failed"); 
    } finally { 
      setLoading(false); 
    } 
  };
  
  const handle2FASuccess = (data) => { onLogin(data); };
  const handle2FAError = (err) => { setError(err.message || 'Verification failed'); };
  const showNotice = (message, type) => { if (process.env.NODE_ENV === 'development') console.log(`[${type}] ${message}`); };
  
  if (requires2FA) {
    return (
      <main className="login-page dark noise-overlay">
        <section className="login-brand-panel">
          <div className="brand-mark"><ShieldCheck size={32} /></div>
          <p className="eyebrow">Secure Access</p>
          <h1>Two-Factor Authentication</h1>
          <p className="login-copy">Enter your verification code to complete sign-in.</p>
        </section>
        <section className="login-card">
          <TwoFactorVerification 
            tempToken={tempToken}
            onSuccess={handle2FASuccess}
            onError={handle2FAError}
            showNotice={showNotice}
          />
        </section>
      </main>
    );
  }
  
  return (
    <main className="login-page dark noise-overlay">
      <section className="login-brand-panel">
        <div className="brand-mark"><ShieldCheck size={32} /></div><p className="eyebrow">Premium Internal Command Center</p><h1>MedSpaBookings OS</h1>
        <p className="login-copy">A black-card executive operating system for lead generation, AI audits, sales execution, onboarding, revenue, and team command.</p>
        <div className="workflow-line">{['Lead Gen', 'AI Audit', 'Pipeline', 'Onboarding', 'Management'].map((step) => <span key={step}>{step}</span>)}</div>
      </section>
      <section className="login-card glass-panel" data-testid="login-panel">
        <div className="login-card-header"><LockKeyhole size={22} /><div><h2>Sign In</h2><p>Enter your credentials to access the platform.</p></div></div>
        <form onSubmit={submit} className="login-form">
          <label>Email<input data-testid="login-email-input" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
          <label>Password<input data-testid="login-password-input" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          {error && <div className="form-error" data-testid="login-error-message">{error}</div>}
          <button className="primary-btn" data-testid="login-submit-button" type="submit" disabled={loading}>{loading ? "Authenticating..." : "Sign In"}</button>
        </form>
        <p style={{ marginTop: 18, fontSize: 14, textAlign: "right" }}><a href="/forgot-password" style={{ color: "var(--gold)" }} data-testid="login-forgot-password-link">Forgot password?</a></p>
        <p style={{ marginTop: 14, fontSize: 13, textAlign: "center", opacity: 0.8 }} data-testid="login-legal-links">
          <a href="/terms" style={{ color: "var(--gold)" }}>Terms of Service</a> &middot; <a href="/privacy" style={{ color: "var(--gold)" }}>Privacy Policy</a> &middot; <a href="/client" style={{ color: "var(--gold)" }} data-testid="login-client-portal-link">Client portal</a>
        </p>
      </section>
    </main>
  );
}

// A team member's password was force-reset (by a Master Admin) or the
// account was seeded requiring one (production Master Admin, first
// login). The backend already exposes both flags on `user` today; until
// now nothing on the frontend read them, so the prompt never appeared -
// this blocks access to the rest of the app until it's cleared.
function ForcePasswordChange({ token, user, onChanged }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: authHeaders(token) }
      );
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page dark noise-overlay">
      <section className="login-brand-panel">
        <div className="brand-mark"><ShieldCheck size={32} /></div>
        <p className="eyebrow">Security Checkpoint</p>
        <h1>Set a New Password</h1>
        <p className="login-copy">Your account requires a new password before you can continue{user?.name ? `, ${user.name}` : ""}.</p>
      </section>
      <section className="login-card glass-panel" data-testid="force-password-change-panel">
        <div className="login-card-header"><LockKeyhole size={22} /><div><h2>Update Password</h2><p>This is required before you can access the platform.</p></div></div>
        <form onSubmit={submit} className="login-form">
          <label>Current Password<input data-testid="force-pw-current" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></label>
          <label>New Password<input data-testid="force-pw-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></label>
          <label>Confirm New Password<input data-testid="force-pw-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></label>
          {error && <div className="form-error" data-testid="force-pw-error">{error}</div>}
          <button className="primary-btn" data-testid="force-pw-submit" type="submit" disabled={loading}>{loading ? "Updating..." : "Update Password"}</button>
        </form>
      </section>
    </main>
  );
}

function AppShell({ user, permissions, active, setActive, onLogout, children, query, setQuery, searchResults, mobileOpen, setMobileOpen, collapsed, setCollapsed }) {
  const handleNav = (id) => { setActive(id); setMobileOpen(false); };
  return (
    <div className={`app-shell dark ${collapsed ? "rail-collapsed" : ""}`}>
      <aside className="left-rail desktop-rail" data-testid="left-rail-navigation"><SidebarContent permissions={permissions} active={active} setActive={handleNav} collapsed={collapsed} setCollapsed={setCollapsed} user={user} /></aside>
      {mobileOpen && <div className="mobile-drawer-backdrop" data-testid="mobile-nav-backdrop"><aside className="mobile-drawer"><SidebarContent permissions={permissions} active={active} setActive={handleNav} collapsed={false} setCollapsed={setCollapsed} user={user} /></aside></div>}
      <div className="main-column">
        <header className="top-command-bar glass-panel">
          <button className="icon-button mobile-menu-button" data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)}><Menu size={18} /></button>
          <div className="top-title"><p className="eyebrow">{user.role}</p><h1>{MODULE_TITLES[active] || "MedSpaBookings OS"}</h1></div>
          <div className="command-actions">
            <div className="search-box command-search"><Search size={16} /><input data-testid="global-search-input" placeholder="Search leads, clients, audits, tasks" value={query} onChange={(e) => setQuery(e.target.value)} />{query && <kbd>⌘K</kbd>}</div>
            <div className="user-pill" data-testid="current-user-pill"><span>{user.name}</span><small>{user.email}</small></div>
            <button className="ghost-btn" data-testid="logout-button" onClick={onLogout}><LogOut size={16} /> Logout</button>
          </div>
        </header>
        {query && <SearchOverlay results={searchResults} setActive={setActive} />}
        <main className="content-area">{children}</main>
      </div>
    </div>
  );
}

function SidebarContent({ permissions, active, setActive, collapsed, setCollapsed, user }) {
  const visibleNav = NAV.filter((item) => isNavItemAllowed(item, permissions, user));
  const groups = GROUP_ORDER.map((group) => [group, visibleNav.filter((item) => item.group === group)]).filter(([, items]) => items.length);
  return <><div className="rail-top"><div className="brand-lockup"><div className="brand-mini">MSB</div>{!collapsed && <div><strong>MedSpaBookings</strong><span>Executive OS</span></div>}</div><button className="icon-button rail-collapse" data-testid="left-rail-collapse-button" onClick={() => setCollapsed(!collapsed)}>{collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}</button></div><div className="role-card">{!collapsed && <><p>Signed in as</p><strong>{user.name}</strong><span>{user.role}</span></>}</div><nav>{groups.map(([group, items]) => <div className="nav-group" key={group}>{!collapsed && <p>{group}</p>}{items.map((item) => { const Icon = item.icon; return <button key={item.id} data-testid={`left-rail-nav-${item.id}-link`} title={item.label} className={`nav-item ${active === item.id ? "active" : ""}`} onClick={() => setActive(item.id)}><Icon size={18} /> {!collapsed && <span>{item.label}</span>} {active === item.id && !collapsed && <ChevronRight size={15} className="nav-chevron" />}</button>; })}</div>)}</nav><div className="future-modules">{!collapsed && <><p>Future-ready</p><span>Lead Scraper • Cold Email • Proposal Generator • Integrations</span></>}</div></>;
}

function SearchOverlay({ results, setActive }) {
  return <div className="search-overlay glass-panel" data-testid="global-search-results"><p className="eyebrow">Global Search</p>{results.length ? results.map((result) => <button key={`${result.collection}-${result.id}`} onClick={() => setActive(result.module)}><strong>{result.title}</strong><span>{labelize(result.collection)} • {result.subtitle}</span></button>) : <div className="empty-cell">No matching leads, clients, audits, or tasks yet.</div>}</div>;
}


function StatTile({ label, value, formatter }) { return <article className="revenue-stat glass-panel"><span>{label}</span><strong>{formatter ? formatter(value) : value}</strong></article>; }
function ProviderPill({ label }) { return <span className="provider-pill">{label}</span>; }

function LeadAcquisitionCenter({ data, team, api, refresh, showNotice, user }) {
  const sampleCsv = "Clinic Name,Owner Name,Phone,Email,Website,Instagram,Location,Services,Reviews,Source\nElite Glow MedSpa,Dr. Anna Pierce,(602) 555-0101,anna@eliteglow.com,https://eliteglow.example,https://instagram.com/eliteglow,Phoenix AZ,Botox and fillers,184,Google Maps\nPure Skin Loft,Maria Banks,(702) 555-0144,maria@pureskinloft.com,https://pureskin.example,https://instagram.com/pureskinloft,Las Vegas NV,Laser facial and injectables,96,Instagram";
  const [rawText, setRawText] = useState(sampleCsv);
  const [preview, setPreview] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState("");
  // Who the committed leads are assigned to. This used to send team[0] -
  // whoever happened to be first in the team list (in practice the Master
  // Admin), so imported leads were invisible to every Sales Rep, whose
  // lead scope is assigned_team_member_id. Empty = the committing user.
  const [importAssigneeId, setImportAssigneeId] = useState("");
  const [importDraft, setImportDraft] = useState({ name: "New MedSpa Lead Import", source: "Google Maps / GBP", import_type: "CSV", provider: "Outscraper", records_total: 25, records_new: 22, duplicates: 3 });
  const createImport = async () => { await api.post("/lead-acquisition/import", { data: importDraft }); showNotice("Import architecture created", "success"); await refresh("lead_acquisition"); };
  const previewImport = async () => { const res = await api.post("/lead-acquisition/import/preview", { filename: importDraft.name.endsWith(".csv") ? importDraft.name : `${importDraft.name}.csv`, source: importDraft.source, import_type: importDraft.import_type, raw_text: rawText }); setPreview(res.data); setSelectedBatch(res.data.batch.id); showNotice("Import preview and validation complete", "success"); await refresh("lead_acquisition"); };
  const uploadImportFile = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/lead-acquisition/import/upload", formData, { params: { source: importDraft.source, import_type: importDraft.import_type } });
      setPreview(res.data);
      setSelectedBatch(res.data.batch.id);
      showNotice("File uploaded, preview and validation complete", "success");
      await refresh("lead_acquisition");
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to upload import file", "error");
    }
  };
  const commitImport = async () => { if (!preview?.batch?.id && !selectedBatch) return; await api.post("/lead-acquisition/import/commit", { batch_id: preview?.batch?.id || selectedBatch, column_mapping: preview?.suggested_mapping || {}, assigned_team_member_id: importAssigneeId || undefined, bulk_action: "create_leads" }); showNotice("Import committed into Lead Database", "success"); await refresh("lead_acquisition"); };
  const runDedupe = async () => { await api.post("/lead-acquisition/dedupe/run", { data: { name: "Manual Dedupe Run", confidence_threshold: 88 } }); showNotice("Deduplication engine run completed", "success"); await refresh("lead_acquisition"); };
  const totals = data?.totals || {};
  return <div className="page-stack revenue-module" data-testid="lead-acquisition-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Architecture-only • No live APIs connected</p><h2>Lead Scraper Manager for Google Maps, Instagram, and Facebook acquisition.</h2><div className="hero-stats"><span>Google Maps / GBP</span><span>Instagram Business Profiles</span><span>Facebook Business Pages</span></div></div>{user.role === "Master Administrator" && <button className="primary-btn" data-testid="lead-import-create-button" onClick={createImport}><Upload size={16}/> Create Import</button>}</section><section className="revenue-stat-grid"><StatTile label="Records Imported" value={totals.records_imported || 0}/><StatTile label="Duplicates Found" value={totals.duplicates_found || 0}/><StatTile label="Qualified Leads" value={totals.qualified_leads || 0}/><StatTile label="Appointments Booked" value={totals.appointments_booked || 0}/><StatTile label="Source Pipeline" value={totals.pipeline_value || 0} formatter={money}/></section><section className="phase3-grid"><div className="panel glass-panel"><div className="panel-header"><h3>Lead Sources</h3><span>Source analytics dashboard</span></div><div className="source-cards">{(data?.sources || []).map((s) => <article key={s.id} className="source-card"><strong>{s.name}</strong><span>{s.channel} • {s.status}</span><div>{(s.provider_options || []).map((p) => <ProviderPill key={p} label={p}/>)}</div><b>{money(s.pipeline_value)} pipeline</b></article>)}</div></div><div className="panel glass-panel"><div className="panel-header"><h3>CSV / Excel Import Architecture</h3><span>Master Admin controlled</span></div><div className="form-grid"><label>Name<input data-testid="import-name-input" value={importDraft.name} onChange={(e)=>setImportDraft({...importDraft,name:e.target.value})}/></label><label>Source<select data-testid="import-source-select" value={importDraft.source} onChange={(e)=>setImportDraft({...importDraft,source:e.target.value})}><option>Google Maps / GBP</option><option>Instagram Business Profiles</option><option>Facebook Business Pages</option></select></label><label>Import Type<select data-testid="import-type-select" value={importDraft.import_type} onChange={(e)=>setImportDraft({...importDraft,import_type:e.target.value})}><option>CSV</option><option>Excel</option><option>Handle Import</option><option>Profile URL Import</option><option>Meta Lead Forms</option></select></label><label>Provider<select data-testid="import-provider-select" value={importDraft.provider} onChange={(e)=>setImportDraft({...importDraft,provider:e.target.value})}><option>Outscraper</option><option>Apify</option><option>BrightData</option><option>Google APIs</option><option>Meta API Architecture</option></select></label></div><label>CSV / Excel Paste Preview<textarea data-testid="import-raw-textarea" value={rawText} onChange={(e)=>setRawText(e.target.value)}/></label><label>Or Upload File (.csv, .xlsx, .xls)<input type="file" accept=".csv,.xlsx,.xls" data-testid="import-upload-input" disabled={user.role !== "Master Administrator"} onChange={(e)=>{ uploadImportFile(e.target.files[0]); e.target.value=""; }}/></label><div className="button-row"><button className="ghost-btn" data-testid="import-preview-button" disabled={user.role !== "Master Administrator"} onClick={previewImport}><Search size={16}/> Preview + Validate</button><select data-testid="import-assignee-select" aria-label="Assign imported leads to" value={importAssigneeId} onChange={(e) => setImportAssigneeId(e.target.value)} disabled={user.role !== "Master Administrator"}><option value="">Assign to: me</option>{(team || []).filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>Assign to: {m.name}{m.role ? ` (${m.role})` : ""}</option>)}</select><button className="primary-btn" data-testid="import-commit-button" disabled={user.role !== "Master Administrator" || !preview} onClick={commitImport}><Download size={16}/> Commit Import</button></div>{preview && <div className="import-preview glass-panel"><strong>Validation Summary</strong><span>{preview.validation_summary.valid} ready • {preview.validation_summary.duplicates} duplicates • {preview.validation_summary.errors} errors</span><div>{Object.entries(preview.suggested_mapping || {}).map(([k,v])=><ProviderPill key={k} label={`${k} → ${v}`}/>)}</div></div>}{user.role !== "Master Administrator" && <p className="muted">Only Master Admin can create imports, preview files, commit imports or connect providers.</p>}</div><div className="panel glass-panel"><div className="panel-header"><h3>Deduplication Engine</h3><button className="ghost-btn small" data-testid="dedupe-run-button" disabled={user.role !== "Master Administrator"} onClick={runDedupe}>Run Dedupe</button></div><ActivityList items={(data?.dedupe_runs || []).map((r)=>({id:r.id,actor_name:r.name,action:r.status,entity_type:"dedupe",details:`${r.duplicates_found} duplicates • ${r.merge_strategy}`}))}/></div><div className="panel glass-panel"><div className="panel-header"><h3>Enrichment Workflow</h3><span>Qualification pipeline</span></div><ActivityList items={(data?.enrichment_workflows || []).flatMap((w)=>(w.steps||[]).map((step,i)=>({id:`${w.id}-${i}`,actor_name:w.name,action:`Step ${i+1}`,entity_type:"enrichment",details:step})))}/></div></section><RecordTable module="lead_acquisition" records={data?.import_batches || []} team={team} onCreate={()=>{}} onUpdate={()=>{}} onDelete={()=>{}} readonly query="" /></div>;
}


function IntegrationCenter(props) {
  return <IntegrationCenterEnhanced {...props} team={props.data?.team || []} />;
}

function OnboardingCenter({ data, api, refresh, showNotice }) {
  const milestones = data?.milestones || ["Contract Signed", "Invoice Paid", "Assets Received", "Website Access", "Google Access", "Meta Access", "CRM Setup", "Automation Setup", "Launch Complete"];
  const progress = data?.client_progress || [];
  const toggle = async (clientId, milestone, completed) => { await api.post(`/onboarding/${clientId}/milestone`, { data: { milestone, completed } }); showNotice("Onboarding milestone updated", "success"); await refresh("onboarding_center"); };
  return <div className="page-stack" data-testid="onboarding-production-center"><section className="module-toolbar glass-panel"><div><p className="eyebrow">Client Success Workflow</p><h2>Client Onboarding Center</h2><span>Contract Signed → Invoice Paid → Assets/Access → CRM/Automation → Launch</span></div><StatTile label="Avg Completion" value={`${data?.summary?.average_completion || 0}%`} /></section><section className="onboarding-board">{progress.map((client)=><article className="panel glass-panel onboarding-card" key={client.client_id}><div className="panel-header"><h3>{client.clinic_name}</h3><span>{client.completion_rate}% complete</span></div>{milestones.map((m)=><label key={m} className="check-row"><input type="checkbox" data-testid={`onboarding-${client.client_id}-${slug(m)}-checkbox`} checked={(client.completed||[]).includes(m)} onChange={(e)=>toggle(client.client_id,m,e.target.checked)}/><span>{m}</span></label>)}<p className="muted">Next: {client.next_milestone}</p></article>)}</section></div>;
}

function RevenueModule({ module, records, team, onCreate, onUpdate, onDelete, query }) {
  const headlines = { cold_email: "Campaigns, sequences, templates, open/reply/follow-up tracking.", cold_calling: "Call queue, scripts, notes, outcomes, and follow-up reminders.", proposal_generator: "MedSpaBookings Growth System proposal architecture and generated proposals.", onboarding_center: "Client onboarding milestones after close.", integrations: "Website, Instagram, Facebook, Google, Meta and email architecture — not connected yet.", inbox: "SMS, WhatsApp, website and client-portal conversations. Instagram/Facebook DMs: coming soon (not connected).", ai_sales: "AI-generated talking points, objections, closing strategy, revenue opportunity and follow-ups." };
  return <div className="page-stack revenue-module" data-testid={`${module}-center`}><section className="module-toolbar glass-panel"><div><p className="eyebrow">Revenue Generation Engine</p><h2>{MODULE_TITLES[module]}</h2><span>{headlines[module]}</span></div>{["cold_email","cold_calling","proposal_generator","onboarding_center","inbox"].includes(module) && <button className="primary-btn" data-testid={`${module}-new-record-button`} onClick={() => onCreate(module,{name:`New ${MODULE_TITLES[module]}`,status:"Draft"})}><Plus size={16}/> New</button>}</section><RecordTable module={module} records={records} team={team} onCreate={onCreate} onUpdate={onUpdate} onDelete={onDelete} readonly={module==="integrations"} query={query}/></div>;
}

function AISalesCenter({ api, refresh, showNotice, records, onUpdate }) {
  const [form, setForm] = useState({ clinic_name: "Radiant Haus Medical Aesthetics", goal: "book_appointment", context: "High-review med spa with strong injectable demand but inconsistent follow-up automation." });
  const [proposal, setProposal] = useState({ clinic_name: "North Shore Skin & Laser", owner_name: "Melissa Kwan", context: "Proposal after AI audit delivery. Wants appointment growth and automation.", setup_fee: 12500, monthly_management: 800, ad_spend: "Separate" });
  const generateAssist = async () => { await api.post("/ai-sales/assist", form); showNotice("AI sales guidance generated", "success"); await refresh("ai_sales"); };
  const generateProposal = async () => { await api.post("/proposals/generate", proposal); showNotice("AI proposal generated", "success"); await refresh("proposal_generator"); };
  return <div className="page-stack" data-testid="ai-sales-assistant"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">AI Sales Assistant + AI Proposal Generator</p><h2>Generate talking points, objection handling, closing strategy, follow-up recommendations and proposals.</h2></div></section><section className="phase3-grid"><div className="panel glass-panel"><div className="panel-header"><h3>AI Sales Assistant</h3><span>Real LLM generation</span></div><label>Clinic<input data-testid="ai-sales-clinic-input" value={form.clinic_name} onChange={(e)=>setForm({...form,clinic_name:e.target.value})}/></label><label>Goal<select data-testid="ai-sales-goal-select" value={form.goal} onChange={(e)=>setForm({...form,goal:e.target.value})}><option>book_appointment</option><option>handle_objection</option><option>close_client</option><option>follow_up</option></select></label><label>Context<textarea data-testid="ai-sales-context-input" value={form.context} onChange={(e)=>setForm({...form,context:e.target.value})}/></label><button className="primary-btn" data-testid="ai-sales-generate-button" onClick={generateAssist}><Bot size={16}/> Generate Sales Guidance</button></div><div className="panel glass-panel"><div className="panel-header"><h3>AI Proposal Generator</h3><span>MedSpaBookings Growth System</span></div><label>Clinic<input data-testid="ai-proposal-clinic-input" value={proposal.clinic_name} onChange={(e)=>setProposal({...proposal,clinic_name:e.target.value})}/></label><label>Owner<input value={proposal.owner_name} onChange={(e)=>setProposal({...proposal,owner_name:e.target.value})}/></label><div className="form-grid"><label>Setup Fee<input type="number" value={proposal.setup_fee} onChange={(e)=>setProposal({...proposal,setup_fee:Number(e.target.value)})}/></label><label>Monthly<input type="number" value={proposal.monthly_management} onChange={(e)=>setProposal({...proposal,monthly_management:Number(e.target.value)})}/></label></div><label>Context<textarea value={proposal.context} onChange={(e)=>setProposal({...proposal,context:e.target.value})}/></label><button className="primary-btn" data-testid="ai-proposal-generate-button" onClick={generateProposal}><FileText size={16}/> Generate Proposal</button></div></section><RecordTable module="ai_sales" records={records.ai_sales || []} team={[]} onCreate={()=>{}} onUpdate={onUpdate} onDelete={()=>{}} readonly query="" /></div>;
}

function KpiCard({ id, label, value, suffix = "", formatter, icon: Icon = TrendingUp }) {
  const display = formatter ? formatter(value) : `${value ?? 0}${suffix}`;
  return <article className="kpi-card glass-panel noise-overlay" data-testid={`ceo-kpi-${id}`}><div className="kpi-icon"><Icon size={18} /></div><p>{label}</p><strong>{display}</strong><span className="kpi-meta">Live internal metric</span></article>;
}
function ChartPanel({ title, subtitle, children, wide = false, testId }) { return <section className={`panel chart-panel glass-panel ${wide ? "wide" : ""}`} data-testid={testId}><div className="panel-header"><div><h3>{title}</h3><span>{subtitle}</span></div></div>{children}</section>; }
const chartTooltip = { background: "rgba(12,12,12,.96)", border: "1px solid rgba(212,166,42,.28)", borderRadius: "12px", color: "#fff" };

function Dashboard({ dashboard, analytics, setActive, user }) {
  if (!dashboard) return <SkeletonDashboard />;
  const k = dashboard.kpis || {};
  const kpis = [["revenue-this-month", "Revenue This Month", k.revenue_this_month, money, "", BadgeDollarSign], ["revenue-this-year", "Revenue This Year", k.revenue_this_year, money, "", BadgeDollarSign], ["pipeline-value", "Pipeline Value", k.pipeline_value, money, "", BarChart3], ["leads-added-today", "Leads Added Today", k.leads_added_today, null, "", Database], ["appointments-booked", "Appointments Booked", k.appointments_booked, null, "", CalendarDays], ["audits-generated", "Audits Generated", k.audits_generated, null, "", Sparkles], ["audits-delivered", "Audits Delivered", k.audits_delivered, null, "", CheckCircle2], ["proposals-sent", "Proposals Sent", k.proposals_sent, null, "", FileText], ["clients-closed", "Clients Closed", k.clients_closed, null, "", Building2], ["close-rate", "Close Rate", k.close_rate, null, "%", TrendingUp], ["active-clients", "Active Clients", k.active_clients, null, "", Building2], ["tasks-due-today", "Tasks Due", k.tasks_due_today, null, "", ClipboardList], ["follow-ups-due-today", "Follow Ups Due", k.follow_ups_due_today, null, "", Activity]];
  const a = analytics || {};
  return <div className="page-stack command-center"><section className="hero-panel ceo-hero glass-panel noise-overlay"><div><p className="eyebrow">{user.role === "Master Administrator" ? "Master Admin Only • CEO Command Center" : "Role Dashboard"}</p><h2>Executive visibility across revenue, sales, clients, team, audits, tasks, appointments, and pipeline.</h2><div className="hero-stats"><span>Sales: {a.counts?.sales ?? k.total_leads}</span><span>Clients: {a.counts?.clients ?? k.active_clients}</span><span>Pipeline: {a.counts?.pipeline ?? 0}</span></div></div><button className="primary-btn" data-testid="dashboard-open-audit-center-button" onClick={() => setActive("audits")}><Sparkles size={17} /> Generate AI Audit</button></section><section className="kpi-grid executive-kpis">{kpis.map(([id, label, value, formatter, suffix, Icon]) => <KpiCard key={id} id={id} label={label} value={value} formatter={formatter} suffix={suffix} icon={Icon} />)}</section>{user.role === "Master Administrator" && <section className="analytics-grid"><ChartPanel title="Revenue Trend" subtitle="Monthly tracked revenue" wide testId="chart-revenue-trend"><ResponsiveContainer width="100%" height={270}><AreaChart data={a.revenue_trend || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Area type="monotone" dataKey="revenue" stroke="#d4a62a" fill="rgba(212,166,42,.2)" /></AreaChart></ResponsiveContainer></ChartPanel><ChartPanel title="Lead Source Performance" subtitle="Sources producing pipeline" testId="chart-lead-source"><ResponsiveContainer width="100%" height={250}><BarChart data={a.lead_source_performance || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="leads" fill="#d4a62a" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></ChartPanel><ChartPanel title="Sales Pipeline" subtitle="Stage value and volume" testId="chart-sales-pipeline"><ResponsiveContainer width="100%" height={250}><BarChart data={a.sales_pipeline || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" tick={{ fontSize: 10 }} /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="value" fill="#d4a62a" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></ChartPanel><ChartPanel title="Conversion Rate" subtitle="Pipeline conversion by stage" testId="chart-conversion-rate"><ResponsiveContainer width="100%" height={250}><LineChart data={a.conversion_rate || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" tick={{ fontSize: 10 }} /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Line type="monotone" dataKey="rate" stroke="#d4a62a" strokeWidth={3} dot={{ fill: "#d4a62a" }} /></LineChart></ResponsiveContainer></ChartPanel><ChartPanel title="Client Growth" subtitle="Cumulative active clients" testId="chart-client-growth"><ResponsiveContainer width="100%" height={250}><AreaChart data={a.client_growth || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Area type="monotone" dataKey="clients" stroke="#f1d98a" fill="rgba(241,217,138,.18)" /></AreaChart></ResponsiveContainer></ChartPanel><ChartPanel title="Team Performance" subtitle="Ownership and execution" testId="chart-team-performance"><ResponsiveContainer width="100%" height={250}><BarChart data={a.team_performance || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" tick={{ fontSize: 10 }} /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="pipeline" fill="#d4a62a" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></ChartPanel><ChartPanel title="Audit Performance" subtitle="Generation and delivery quality" testId="chart-audit-performance"><ResponsiveContainer width="100%" height={250}><BarChart data={a.audit_performance || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="value" fill="#d4a62a" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></ChartPanel><section className="panel activity-rail glass-panel"><div className="panel-header"><h3>Executive Activity Feed</h3><span>Live activity tracking</span></div><ActivityList items={a.activity_feed || dashboard.recent_activity || []} /></section></section>}</div>;
}


// ===============================================
// ERROR BOUNDARY
// ===============================================

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    // Best-effort report to the admin error-monitoring pipeline (feeds
    // /monitoring/alerts' critical-error-spike check and /system/health's
    // recent_errors panel, both already wired into SystemHealthDashboard -
    // but nothing ever populated error_logs before now). This boundary
    // wraps the entire app - team portal of any role, client portal, and
    // unauthenticated public pages alike - so a token may be missing
    // entirely or belong to a non-Master-Admin/non-team user; the route
    // is intentionally Master-Admin-only, so a missing token or a
    // 401/403/network failure here is expected and silently ignored
    // rather than surfaced, since this must never itself throw inside
    // componentDidCatch.
    const token = localStorage.getItem("msb_token");
    if (!token) return;
    fetch(`${API}/monitoring/log-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        severity: "critical",
        error_type: "react_render_error",
        message: error?.message || String(error),
        stack_trace: error?.stack,
        endpoint: window.location.pathname,
        metadata: { component_stack: errorInfo?.componentStack },
      }),
    }).catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="page-stack" data-testid="error-boundary-fallback">
          <section className="panel glass-panel" style={{padding: "2rem", textAlign: "center"}}>
            <ShieldAlert size={48} color="var(--destructive)" style={{margin: "0 auto 1rem"}} />
            <h2>Something went wrong</h2>
            <p style={{color: "var(--muted)", margin: "1rem 0"}}>
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <button 
              className="primary-btn" 
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload-button"
            >
              Reload Application
            </button>
          </section>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===============================================
// STRIPE PRODUCTION BILLING CENTER
// ===============================================

// Inline placeholder for one Billing Center section whose request failed,
// so the rest of the page still renders with the data that did load.
function SectionLoadError({ label, onRetry }) {
  return (
    <div className="empty-cell" data-testid="billing-section-load-error">
      Couldn't load {label}.{" "}
      <button className="ghost-btn small" onClick={onRetry}>Retry</button>
    </div>
  );
}

function StripeBillingCenter({ api, showNotice }) {
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [failedWebhooks, setFailedWebhooks] = useState([]);
  const [webhookFailureAlerts, setWebhookFailureAlerts] = useState([]);
  const [mrrMetrics, setMrrMetrics] = useState(null);
  const [revenueDashboard, setRevenueDashboard] = useState(null);
  const [failedPayments, setFailedPayments] = useState([]);
  const [stripeHealth, setStripeHealth] = useState(null);
  const [view, setView] = useState("dashboard");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [testingPayment, setTestingPayment] = useState(false);
  // Refund manager: the transaction being refunded, the form, and the
  // idempotency key generated once per dialog (so a double click or retry
  // of the same dialog can never refund twice).
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", reason: "requested_by_customer", note: "", confirmText: "" });
  const [refundKey, setRefundKey] = useState("");
  const [refunding, setRefunding] = useState(false);
  
  // Keys of billing sections whose last load failed, e.g. { health: true }.
  // Sections render an inline "couldn't load" state instead of misleading
  // zeros/empty lists ("No failed payments") when their own request failed.
  const [loadErrors, setLoadErrors] = useState({});

  // money() rounds to whole dollars; refunds need cents.
  const usd = (value) => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const refundableCents = (tx) => Math.max(0, Math.round(Number(tx.amount || 0) * 100) - Number(tx.refunded_cents || 0));

  const newRefundKey = () => `refund-${(window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;

  const openRefund = (tx) => {
    setRefundTarget(tx);
    setRefundForm({ amount: "", reason: "requested_by_customer", note: "", confirmText: "" });
    setRefundKey(newRefundKey());
  };

  const submitRefund = async () => {
    if (!refundTarget || refunding) return;
    const remaining = refundableCents(refundTarget);
    const amount = refundForm.amount.trim() === "" ? null : Number(refundForm.amount);
    if (amount !== null && (!Number.isFinite(amount) || amount <= 0 || Math.round(amount * 100) > remaining)) {
      showNotice(`Enter an amount between $0.01 and ${usd(remaining / 100)}`, "error");
      return;
    }
    setRefunding(true);
    try {
      await api.post("/stripe/refunds", {
        transaction_id: refundTarget.id,
        amount,
        reason: refundForm.reason,
        note: refundForm.note || null,
        confirm: true,
        idempotency_key: refundKey,
      });
      showNotice("Refund issued", "success");
      setRefundTarget(null);
      loadData();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Refund failed", "error");
      // Stripe answered "no" (502) or the server says this key already failed:
      // that key is spent and can only ever replay the failure, so the next
      // click must be a new request. Network errors / "in progress" keep the
      // key on purpose - the first attempt may still have reached Stripe.
      const detail = String(err.response?.data?.detail || "");
      if (err.response?.status === 502 || /earlier attempt .* failed/i.test(detail)) setRefundKey(newRefundKey());
    } finally {
      setRefunding(false);
    }
  };

  const loadData = useCallback(async () => {
    // Each request settles independently (Promise.allSettled) so one slow or
    // failing endpoint - e.g. /stripe/health when Stripe isn't configured -
    // only affects its own section. Previously a single rejection inside
    // Promise.all skipped every setState below and blanked the whole page.
    const sources = [
      { key: "transactions", label: "transactions", path: "/stripe/transactions", apply: (d) => setTransactions(d.transactions || []) },
      { key: "customers", label: "customers", path: "/stripe/customers", apply: (d) => setCustomers(d.customers || []) },
      { key: "webhookEvents", label: "webhook events", path: "/stripe/webhook-events", apply: (d) => setWebhookEvents(d.events || []) },
      { key: "failedWebhooks", label: "failed webhooks", path: "/stripe/webhook/failed", apply: (d) => setFailedWebhooks(d.failed_webhooks || []) },
      { key: "webhookAlerts", label: "webhook alerts", path: "/stripe/alerts/webhook-failures", apply: (d) => setWebhookFailureAlerts(d.alerts || []) },
      { key: "mrr", label: "MRR metrics", path: "/stripe/metrics/mrr", apply: (d) => setMrrMetrics(d) },
      { key: "revenue", label: "revenue dashboard", path: "/stripe/metrics/revenue-dashboard", apply: (d) => setRevenueDashboard(d) },
      { key: "failedPayments", label: "failed payments", path: "/stripe/metrics/failed-payments", apply: (d) => setFailedPayments(d.failed_payments || []) },
      { key: "health", label: "Stripe health", path: "/stripe/health", apply: (d) => setStripeHealth(d) },
    ];
    const results = await Promise.allSettled(sources.map((source) => api.get(source.path)));
    const errors = {};
    const failed = [];
    results.forEach((result, index) => {
      const source = sources[index];
      const data = result.status === "fulfilled" ? result.value.data : null;
      if (data && typeof data === "object") {
        source.apply(data);
      } else {
        errors[source.key] = true;
        failed.push({ source, reason: result.status === "rejected" ? result.reason : null });
      }
    });
    setLoadErrors(errors);
    if (failed.length === sources.length) {
      showNotice(failed[0].reason?.response?.data?.detail || "Failed to load billing data", "error");
    } else if (failed.length) {
      showNotice(`Some billing data couldn't load: ${failed.map((f) => f.source.label).join(", ")}`, "warning");
    }
  }, [api, showNotice]);
  
  useEffect(() => { loadData(); }, [loadData]);

  // Stat values for a section whose request failed show "—" rather than a
  // misleading $0 / 0.
  const statValue = (key, value, formatter) => (loadErrors[key] ? "—" : formatter ? formatter(value) : value);

  const [retryingId, setRetryingId] = useState(null);
  const retryWebhook = async (eventId) => {
    setRetryingId(eventId);
    try {
      const res = await api.post(`/stripe/webhook/${eventId}/retry`);
      showNotice(res.data.status === "success" ? "Webhook reconciled successfully" : (res.data.message || "Nothing to reconcile for this event"), res.data.status === "success" ? "success" : "info");
      await loadData();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Retry failed", "error");
    } finally {
      setRetryingId(null);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      showNotice("Payment status check timed out. Check transactions list.", "warning");
      setCheckingStatus(false);
      return;
    }
    
    try {
      const res = await api.get(`/stripe/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        showNotice("✅ Payment successful! Client record created and onboarding tasks generated.", "success");
        await loadData();
        setCheckingStatus(false);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (res.data.status === "expired") {
        showNotice("Payment session expired", "error");
        setCheckingStatus(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      showNotice("Error checking payment status", "error");
      setCheckingStatus(false);
    }
  }, [api, showNotice, loadData]);

  // Check payment status (poll for success after redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const status = urlParams.get("status");
    
    if (sessionId && !checkingStatus) {
      setCheckingStatus(true);
      pollPaymentStatus(sessionId, 0);
    } else if (status === "canceled") {
      showNotice("Payment was canceled", "warning");
    }
  }, [checkingStatus, pollPaymentStatus, showNotice]);
  
  return (
    <div className="page-stack" data-testid="stripe-billing-center">
      <section className="hero-panel ceo-hero glass-panel">
        <div>
          <p className="eyebrow">Master Admin Only • Stripe Production Billing</p>
          <h2>Stripe Billing Center</h2>
          <div className="hero-stats">
            <span>MRR: {loadErrors.mrr ? "—" : `$${mrrMetrics?.mrr?.toFixed(2) || 0}`}</span>
            <span>ARR: {loadErrors.mrr ? "—" : `$${mrrMetrics?.arr?.toFixed(2) || 0}`}</span>
            <span>{statValue("transactions", transactions.filter(t => ["paid", "partially_refunded"].includes(t.payment_status)).length)} successful</span>
            <span>{statValue("failedPayments", failedPayments.length)} failed</span>
          </div>
        </div>
        <div style={{display: "flex", gap: "0.5rem"}}>
          <button className={view === "dashboard" ? "primary-btn" : "ghost-btn"} onClick={() => setView("dashboard")} data-testid="billing-view-dashboard-button">Dashboard</button>
          <button className={view === "transactions" ? "primary-btn" : "ghost-btn"} onClick={() => setView("transactions")} data-testid="billing-view-transactions-button">Transactions</button>
          <button className={view === "customers" ? "primary-btn" : "ghost-btn"} onClick={() => setView("customers")} data-testid="billing-view-customers-button">Customers</button>
          <button className={view === "webhooks" ? "primary-btn" : "ghost-btn"} onClick={() => setView("webhooks")} data-testid="billing-view-webhooks-button">Webhooks</button>
        </div>
      </section>
      
      {view === "dashboard" && (
        <>
          <section className="revenue-stat-grid">
            <StatTile label="Total Revenue (net of refunds)" value={statValue("revenue", revenueDashboard?.total_revenue || 0, money)} />
            <StatTile label="Refunded" value={statValue("revenue", revenueDashboard?.total_refunded || 0, money)} />
            <StatTile label="Setup Fees" value={statValue("revenue", revenueDashboard?.total_setup_fees || 0, money)} />
            <StatTile label="Monthly Revenue" value={statValue("revenue", revenueDashboard?.total_monthly_revenue || 0, money)} />
            <StatTile label="This Month" value={statValue("revenue", revenueDashboard?.this_month_revenue || 0, money)} />
            <StatTile label="MRR" value={statValue("mrr", mrrMetrics?.mrr || 0, money)} />
            <StatTile label="ARR" value={statValue("mrr", mrrMetrics?.arr || 0, money)} />
            <StatTile label="Paying Clients" value={statValue("mrr", mrrMetrics?.paying_clients || 0)} />
            <StatTile label="Failed Payments" value={statValue("failedPayments", failedPayments.length)} />
          </section>
          
          <section className="phase3-grid">
            <BillingCheckoutLinkPanel api={api} showNotice={showNotice} />
            
            <div className="panel glass-panel">
              <div className="panel-header">
                <h3>Stripe Health</h3>
              </div>
              {loadErrors.health && <SectionLoadError label="Stripe health" onRetry={loadData} />}
              <div className="compact-list">
                {Object.entries(stripeHealth?.checks || {}).map(([key, check]) => (
                  <div key={key} className="compact-row">
                    {check.status === "healthy" ? <CheckCircle2 size={16} color="var(--success)" /> : <ShieldAlert size={16} color="var(--warning)" />}
                    <div>
                      <strong>{labelize(key)}</strong>
                      <span>{JSON.stringify(check)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          <section className="panel glass-panel">
            <div className="panel-header">
              <h3>Recent Failed Payments</h3>
            </div>
            {loadErrors.failedPayments ? (
              <SectionLoadError label="failed payments" onRetry={loadData} />
            ) : failedPayments.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {failedPayments.slice(0, 10).map(tx => (
                    <tr key={tx.id}>
                      <td>{tx.package_name}</td>
                      <td>{money(tx.amount)}</td>
                      <td><span className="status-badge destructive">{tx.payment_status}</span></td>
                      <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                      <td>{tx.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-cell">No failed payments 🎉</div>
            )}
          </section>
        </>
      )}
      
      {view === "transactions" && (
        <section className="panel glass-panel">
          <div className="panel-header">
            <h3>All Transactions ({transactions.length})</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Package</th>
                <th>Amount</th>
                <th>Payment Status</th>
                <th>Status</th>
                <th>Date</th>
                <th>Created By</th>
                <th>Session ID</th>
                <th>Refund</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td>{tx.package_name}</td>
                  <td>{money(tx.amount)}</td>
                  <td>
                    <span className={`status-badge ${tx.payment_status === "paid" ? "success" : (tx.payment_status === "pending" || tx.payment_status === "partially_refunded") ? "warning" : "destructive"}`}>{tx.payment_status}</span>
                    {tx.refunded_cents > 0 && <div style={{fontSize: "0.75rem"}}>{usd(tx.refunded_cents / 100)} refunded</div>}
                  </td>
                  <td>{tx.status}</td>
                  <td>{new Date(tx.created_at).toLocaleString()}</td>
                  <td>{tx.created_by}</td>
                  <td><code style={{fontSize: "0.75rem"}}>{tx.session_id?.substring(0, 20)}...</code></td>
                  <td>
                    {["paid", "partially_refunded"].includes(tx.payment_status) && refundableCents(tx) > 0 && (
                      <button className="ghost-btn small" data-testid={`billing-refund-${tx.id}-button`} onClick={() => openRefund(tx)}>Refund</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {refundTarget && (
            <div className="modal-backdrop" role="dialog" aria-modal="true" data-testid="billing-refund-dialog"
                 style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000}}>
              <div className="panel glass-panel" style={{maxWidth: 460, width: "92%", padding: 24}}>
                <h3>Refund {refundTarget.package_name || "payment"}</h3>
                <p style={{fontSize: "0.85rem"}}>
                  Paid {usd(refundTarget.amount)}; refundable now <strong>{usd(refundableCents(refundTarget) / 100)}</strong>.
                  This sends money back to the customer through Stripe and cannot be undone.
                </p>
                <label>Amount (leave empty to refund the full {usd(refundableCents(refundTarget) / 100)})
                  <input type="number" min="0.01" step="0.01" value={refundForm.amount} data-testid="billing-refund-amount"
                         onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} />
                </label>
                <label>Reason
                  <select value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}>
                    <option value="requested_by_customer">Requested by customer</option>
                    <option value="duplicate">Duplicate</option>
                    <option value="fraudulent">Fraudulent</option>
                  </select>
                </label>
                <label>Internal note
                  <input type="text" maxLength={500} value={refundForm.note} onChange={(e) => setRefundForm({ ...refundForm, note: e.target.value })} />
                </label>
                <label>Type REFUND to confirm
                  <input type="text" value={refundForm.confirmText} data-testid="billing-refund-confirm-text"
                         onChange={(e) => setRefundForm({ ...refundForm, confirmText: e.target.value })} />
                </label>
                <div className="button-row" style={{marginTop: 16}}>
                  <button className="primary-btn small" data-testid="billing-refund-submit" disabled={refunding || refundForm.confirmText !== "REFUND"} onClick={submitRefund}>
                    {refunding ? "Refunding..." : "Issue refund"}
                  </button>
                  <button className="ghost-btn small" disabled={refunding} onClick={() => setRefundTarget(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {loadErrors.transactions && <SectionLoadError label="transactions" onRetry={loadData} />}
        </section>
      )}
      
      {view === "customers" && (
        <section className="panel glass-panel">
          <div className="panel-header">
            <h3>Stripe Customers ({customers.length})</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Created</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(cust => (
                <tr key={cust.id}>
                  <td>{cust.name}</td>
                  <td>{cust.email}</td>
                  <td>{cust.phone || "—"}</td>
                  <td>{new Date(cust.created_at).toLocaleString()}</td>
                  <td>{cust.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loadErrors.customers ? <SectionLoadError label="customers" onRetry={loadData} /> : !customers.length && <div className="empty-cell">No customers yet</div>}
        </section>
      )}
      
      {view === "webhooks" && (
        <>
          {(webhookFailureAlerts.length > 0 || loadErrors.webhookAlerts) && (
            <section className="panel glass-panel" data-testid="webhook-failure-alerts">
              <div className="panel-header">
                <h3>Webhook Alerts</h3>
              </div>
              {loadErrors.webhookAlerts && <SectionLoadError label="webhook alerts" onRetry={loadData} />}
              <div className="compact-list">
                {webhookFailureAlerts.map((alert, index) => (
                  <div key={index} className="compact-row">
                    <ShieldAlert size={16} color={alert.severity === "critical" ? "var(--destructive)" : "var(--warning)"} />
                    <div>
                      <strong>{alert.message}</strong>
                      {alert.action && <span>{alert.action}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="panel glass-panel">
            <div className="panel-header">
              <h3>Failed Webhooks ({failedWebhooks.length})</h3>
            </div>
            {loadErrors.failedWebhooks ? (
              <SectionLoadError label="failed webhooks" onRetry={loadData} />
            ) : failedWebhooks.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Event Type</th>
                    <th>Payment Status</th>
                    <th>Error</th>
                    <th>Retry Count</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {failedWebhooks.map(event => (
                    <tr key={event.id}>
                      <td>{event.event_type}</td>
                      <td><span className="status-badge warning">{event.payment_status || "unknown"}</span></td>
                      <td>{event.last_error || "—"}</td>
                      <td>{event.retry_count || 0}</td>
                      <td>{new Date(event.created_at).toLocaleString()}</td>
                      <td>
                        <button
                          className="ghost-btn small"
                          data-testid={`retry-webhook-${event.id}-button`}
                          onClick={() => retryWebhook(event.id)}
                          disabled={retryingId === event.id}
                        >
                          {retryingId === event.id ? "Retrying..." : "Retry"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-cell">No failed webhooks 🎉</div>
            )}
          </section>

          <section className="panel glass-panel">
            <div className="panel-header">
              <h3>Webhook Events ({webhookEvents.length})</h3>
            </div>
          <table>
            <thead>
              <tr>
                <th>Event Type</th>
                <th>Payment Status</th>
                <th>Session ID</th>
                <th>Processed</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {webhookEvents.map(event => (
                <tr key={event.id}>
                  <td>{event.event_type}</td>
                  <td><span className={`status-badge ${event.payment_status === "paid" ? "success" : "warning"}`}>{event.payment_status}</span></td>
                  <td><code style={{fontSize: "0.75rem"}}>{event.session_id?.substring(0, 20)}...</code></td>
                  <td>{event.processed ? "✅ Yes" : "⏳ No"}</td>
                  <td>{new Date(event.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loadErrors.webhookEvents ? <SectionLoadError label="webhook events" onRetry={loadData} /> : !webhookEvents.length && <div className="empty-cell">No webhook events yet</div>}
          </section>
        </>
      )}
    </div>
  );
}

// ===============================================
// PHASE 7: PRODUCTION READINESS CENTERS
// ===============================================

// 1. System Health Center
function SystemHealthCenter({ health, api, showNotice }) {
  const [data, setData] = useState(health || null);
  const [loading, setLoading] = useState(false);
  const checkHealth = useCallback(async () => { setLoading(true); try { const res = await api.get("/system/health"); setData(res.data); showNotice("Health check complete", "success"); } catch (err) { showNotice(err.response?.data?.detail || "Health check failed", "error"); } finally { setLoading(false); } }, [api, showNotice]);
  useEffect(() => { if (!data) checkHealth(); }, [data, checkHealth]);
  const services = data?.services || {};
  const statusColor = (status) => status === "healthy" ? "var(--success)" : status === "degraded" ? "var(--warning)" : "var(--destructive)";
  return <div className="page-stack" data-testid="system-health-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Real Internal Health Checks</p><h2>System Health Center</h2><div className="hero-stats"><span>Backend</span><span>Database</span><span>AI Engine</span><span>Integrations</span></div></div><button className="primary-btn" data-testid="health-check-button" onClick={checkHealth} disabled={loading}><Server size={16}/> {loading ? "Checking..." : "Run Health Check"}</button></section><section className="revenue-stat-grid"><StatTile label="Overall Status" value={data?.status || "Unknown"} /><StatTile label="Last Check" value={data?.timestamp ? new Date(data.timestamp).toLocaleString() : "—"} /><StatTile label="Services Monitored" value={Object.keys(services).length} /></section><section className="phase3-grid">{Object.entries(services).map(([service, info])=><article key={service} className="panel glass-panel"><div className="panel-header"><h3>{labelize(service)}</h3><span style={{color: statusColor(info.status)}}>{labelize(info.status)}</span></div><div className="form-grid">{Object.entries(info).filter(([key])=>key!=="status").map(([key,value])=><div key={key}><strong>{labelize(key)}</strong><span>{typeof value === "object" ? JSON.stringify(value) : String(value)}</span></div>)}</div></article>)}</section></div>;
}

// 2. Backup & Recovery Center
function BackupRecoveryCenter({ api, showNotice }) {
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState(["leads", "clients", "audits", "tasks", "revenue", "team", "activities", "permissions"]);
  const loadHistory = useCallback(async () => { try { const res = await api.get("/backups/history"); setHistory(res.data.jobs || []); } catch (err) { showNotice(err.response?.data?.detail || "Failed to load backup history", "error"); } }, [api, showNotice]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  const exportBackup = async () => { setExporting(true); try { const res = await api.post("/backups/export", { data: { collections: selectedCollections } }); const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" }); const url = window.URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `medspa-backup-${res.data.metadata.export_id}.json`; a.click(); window.URL.revokeObjectURL(url); showNotice("Backup exported successfully", "success"); await loadHistory(); } catch (err) { showNotice(err.response?.data?.detail || "Export failed", "error"); } finally { setExporting(false); } };
  const collections = ["leads", "clients", "audits", "tasks", "revenue", "team", "activities", "permissions", "documents", "sops"];
  const toggleCollection = (col) => setSelectedCollections(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);

  // Server-side manual backups - a separate system from the export-on-demand
  // one above: /admin/backup/create persists a rate-limited, audit-logged
  // snapshot in GridFS (server.py) rather than just streaming a one-off file
  // to the browser. Was previously unreachable from the UI entirely.
  const [manualBackups, setManualBackups] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [creatingManual, setCreatingManual] = useState(false);
  const loadManualBackups = useCallback(async () => {
    try {
      const [listRes, statusRes] = await Promise.all([api.get("/admin/backup/list"), api.get("/admin/backup/status")]);
      setManualBackups(listRes.data.backups || []);
      setBackupStatus(statusRes.data);
    } catch (err) { showNotice(err.response?.data?.detail || "Failed to load server backups", "error"); }
  }, [api, showNotice]);
  useEffect(() => { loadManualBackups(); }, [loadManualBackups]);
  const createManualBackup = async () => {
    setCreatingManual(true);
    try {
      await api.post("/admin/backup/create");
      showNotice("Server backup created", "success");
      await loadManualBackups();
    } catch (err) { showNotice(err.response?.data?.detail || "Backup creation failed", "error"); }
    finally { setCreatingManual(false); }
  };
  const downloadManualBackup = async (backupId) => {
    try {
      const res = await api.get(`/admin/backup/${backupId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${backupId}.json`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { showNotice(err.response?.data?.detail || "Download failed", "error"); }
  };

  return <div className="page-stack" data-testid="backup-recovery-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Actual Downloadable JSON Exports</p><h2>Backup & Recovery Center</h2><div className="hero-stats"><span>{selectedCollections.length} collections selected</span><span>{history.length} backups in history</span></div></div><button className="primary-btn" data-testid="backup-export-button" onClick={exportBackup} disabled={exporting || selectedCollections.length === 0}><Download size={16}/> {exporting ? "Exporting..." : "Export Backup"}</button></section><section className="phase3-grid"><div className="panel glass-panel"><div className="panel-header"><h3>Select Collections to Export</h3></div>{collections.map(col=><label key={col} className="check-row"><input type="checkbox" data-testid={`backup-collection-${col}-checkbox`} checked={selectedCollections.includes(col)} onChange={()=>toggleCollection(col)}/><span>{labelize(col)}</span></label>)}</div><div className="panel glass-panel"><div className="panel-header"><h3>Backup History</h3></div><div className="compact-list">{history.map(job=><div key={job.id} className="compact-row"><Download size={16}/><div><strong>{new Date(job.created_at).toLocaleString()}</strong><span>{job.total_records} records • {job.collections.join(", ")}</span></div></div>)}{!history.length && <div className="empty-cell">No backups yet. Export your first backup above.</div>}</div></div></section><section className="panel glass-panel" data-testid="server-backups-panel"><div className="panel-header"><h3>Server-Side Backups</h3><span>{backupStatus?.total_backups ?? 0} stored • rate-limited to 3/hour</span></div><button className="primary-btn" data-testid="server-backup-create-button" onClick={createManualBackup} disabled={creatingManual}><Download size={16}/> {creatingManual ? "Creating..." : "Create Server Backup"}</button><div className="compact-list" style={{marginTop: "16px"}}>{manualBackups.map(b=><div key={b.backup_id} className="compact-row"><Download size={16}/><div><strong>{new Date(b.created_at).toLocaleString()}</strong><span>{b.total_documents} documents • {Object.keys(b.collections || {}).length} collections</span></div>{b.grid_file_id ? <button className="ghost-btn small" data-testid={`server-backup-download-${b.backup_id}-button`} onClick={()=>downloadManualBackup(b.backup_id)}>Download</button> : <span className="empty-cell">No stored data</span>}</div>)}{!manualBackups.length && <div className="empty-cell">No server-side backups yet. Create your first one above.</div>}</div></section></div>;
}

// 3. Notification Center
function NotificationCenterPage({ api, showNotice }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const load = useCallback(async () => { try { const res = await api.get("/notifications"); setNotifications(res.data.notifications || []); setUnreadCount(res.data.unread_count || 0); } catch (err) { showNotice(err.response?.data?.detail || "Failed to load notifications", "error"); } }, [api, showNotice]);
  useEffect(() => { load(); }, [load]);
  const markRead = async (id) => { try { await api.post(`/notifications/${id}/read`); await load(); showNotice("Notification marked as read", "success"); } catch (err) { showNotice("Failed to mark notification", "error"); } };
  const markAllRead = async () => { try { await api.post("/notifications/mark-all-read"); await load(); showNotice("All notifications marked as read", "success"); } catch (err) { showNotice("Failed to mark all notifications", "error"); } };
  return <div className="page-stack" data-testid="notification-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Stored In-App Notifications</p><h2>Notification Center</h2><div className="hero-stats"><span>{unreadCount} unread</span><span>{notifications.length} total</span></div></div><button className="ghost-btn" data-testid="notifications-mark-all-read-button" onClick={markAllRead}><Bell size={16}/> Mark All Read</button></section><section className="panel glass-panel"><div className="panel-header"><h3>Recent Notifications</h3></div><div className="compact-list">{notifications.map(notif=><div key={notif.id} className="compact-row" style={{opacity: notif.read ? 0.6 : 1}}><Bell size={16}/><div><strong>{notif.title}</strong><span>{notif.message} • {new Date(notif.created_at).toLocaleString()}</span></div>{!notif.read && <button className="ghost-btn small" data-testid={`notification-mark-read-${notif.id}-button`} onClick={()=>markRead(notif.id)}>Mark Read</button>}</div>)}{!notifications.length && <div className="empty-cell">No notifications yet. Notifications are generated when leads are created, proposals are generated, and automations run.</div>}</div></section></div>;
}

// 4. Workflow Automation Center
function QATestingCenter({ api, showNotice }) {
  const [reports, setReports] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [runs, setRuns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", status: "Passed", notes: "" });
  const [logging, setLogging] = useState(false);
  const load = useCallback(async () => { try { const [reportsRes, readinessRes, runsRes] = await Promise.all([api.get("/qa/test-reports"), api.get("/qa/readiness-score"), api.get("/qa/test-runs")]); setReports(reportsRes.data.reports || []); setReadiness(readinessRes.data); setRuns(runsRes.data.runs || []); } catch (err) { showNotice(err.response?.data?.detail || "Failed to load QA data", "error"); } }, [api, showNotice]);
  useEffect(() => { load(); }, [load]);
  const logRun = async () => {
    if (!form.name.trim()) { showNotice("Give this QA run a name", "error"); return; }
    setLogging(true);
    try {
      await api.post("/qa/run-log", { data: form });
      showNotice("QA run logged", "success");
      setForm({ name: "", status: "Passed", notes: "" });
      setShowForm(false);
      load();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to log QA run", "error");
    } finally {
      setLogging(false);
    }
  };
  const latestReport = reports[0];
  return <div className="page-stack" data-testid="qa-testing-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Test Run History + Production Readiness Scoring</p><h2>QA & Testing Center</h2><div className="hero-stats"><span>{reports.length} test reports</span><span>{readiness?.overall_score || 0}% ready</span></div></div><button className="primary-btn" data-testid="qa-log-run-toggle-button" onClick={()=>setShowForm(!showForm)}><Plus size={16}/> {showForm ? "Cancel" : "Log QA Run"}</button></section>{showForm && <section className="panel glass-panel"><div className="panel-header"><h3>New Manual QA Run</h3></div><label>Name<input data-testid="qa-run-name-input" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></label><label>Status<select data-testid="qa-run-status-select" value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})}><option>Passed</option><option>Failed</option><option>Blocked</option></select></label><label>Notes<textarea data-testid="qa-run-notes-textarea" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></label><button className="primary-btn" data-testid="qa-run-save-button" onClick={logRun} disabled={logging}><Save size={16}/> {logging ? "Saving..." : "Save QA Run"}</button></section>}<section className="revenue-stat-grid"><StatTile label="Production Readiness" value={`${readiness?.overall_score || 0}%`} /><StatTile label="Last Test Report" value={latestReport?.file || "None"} /><StatTile label="Criteria Passed" value={`${Object.values(readiness?.criteria || {}).filter(c=>c.passed).length}/${Object.keys(readiness?.criteria || {}).length}`} /></section><section className="phase3-grid"><div className="panel glass-panel"><div className="panel-header"><h3>Readiness Checklist</h3></div><div className="compact-list">{Object.entries(readiness?.criteria || {}).map(([key,criterion])=><div key={key} className="compact-row">{criterion.passed ? <CheckCircle2 size={16} color="var(--success)"/> : <X size={16} color="var(--destructive)"/>}<div><strong>{labelize(key)}</strong><span>Weight: {criterion.weight}%</span></div></div>)}</div></div><div className="panel glass-panel"><div className="panel-header"><h3>Test Report History</h3></div><div className="compact-list">{reports.map(report=><div key={report.file} className="compact-row"><FileText size={16}/><div><strong>{report.file}</strong><span>{report.data?.backend?.status || "Unknown"} backend • {report.data?.frontend?.status || "Unknown"} frontend</span></div></div>)}{!reports.length && <div className="empty-cell">No test reports found.</div>}</div></div><div className="panel glass-panel"><div className="panel-header"><h3>Recent Manual QA Runs</h3></div><div className="compact-list">{runs.map(run=><div key={run.id} className="compact-row">{run.status === "Passed" ? <CheckCircle2 size={16} color="var(--success)"/> : <X size={16} color="var(--destructive)"/>}<div><strong>{run.name}</strong><span>{run.status} • {run.created_by} • {new Date(run.created_at).toLocaleString()}</span>{run.notes && <span>{run.notes}</span>}</div></div>)}{!runs.length && <div className="empty-cell">No manual QA runs logged yet.</div>}</div></div></section></div>;
}

// 6. Master Admin Analytics Center
function MasterAdminAnalyticsCenter({ api, showNotice }) {
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [pipelineForecast, setPipelineForecast] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const load = useCallback(async () => { try { const [revRes, pipeRes, teamRes] = await Promise.all([api.get("/admin-analytics/revenue-forecast"), api.get("/admin-analytics/pipeline-forecast"), api.get("/admin-analytics/team-performance")]); setRevenueForecast(revRes.data); setPipelineForecast(pipeRes.data); setTeamPerformance(teamRes.data.team_performance || []); } catch (err) { showNotice(err.response?.data?.detail || "Failed to load admin analytics", "error"); } }, [api, showNotice]);
  useEffect(() => { load(); }, [load]);
  return <div className="page-stack" data-testid="admin-analytics-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Revenue Forecasting + Team Performance</p><h2>Master Admin Analytics Center</h2><div className="hero-stats"><span>Revenue Forecast</span><span>Pipeline Forecast</span><span>Team Performance</span></div></div></section><section className="revenue-stat-grid"><StatTile label="Committed Revenue" value={revenueForecast?.committed_revenue || 0} formatter={money} /><StatTile label="Weighted Pipeline" value={revenueForecast?.weighted_pipeline || 0} formatter={money} /><StatTile label="Total Forecast" value={revenueForecast?.total_forecast || 0} formatter={money} /></section><ChartPanel title="Pipeline Forecast by Stage" subtitle="Weighted close probability" wide testId="chart-pipeline-forecast"><ResponsiveContainer width="100%" height={270}><BarChart data={pipelineForecast?.forecast_by_stage || []}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="stage" stroke="#8b8780" tick={{fontSize:10}} /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="weighted_value" fill="#d4a62a" radius={[8,8,0,0]} /></BarChart></ResponsiveContainer></ChartPanel><ChartPanel title="Team Performance Summary" subtitle="Pipeline owned + deals closed" wide testId="chart-team-performance-admin"><ResponsiveContainer width="100%" height={270}><BarChart data={teamPerformance}><CartesianGrid stroke="rgba(255,255,255,.07)" /><XAxis dataKey="name" stroke="#8b8780" tick={{fontSize:10}} /><YAxis stroke="#8b8780" /><Tooltip contentStyle={chartTooltip} /><Bar dataKey="pipeline_owned" fill="#d4a62a" radius={[8,8,0,0]} /></BarChart></ResponsiveContainer></ChartPanel><section className="panel glass-panel"><div className="panel-header"><h3>Team Performance Details</h3></div><table><thead><tr><th>Name</th><th>Role</th><th>Pipeline Owned</th><th>Deals Closed</th><th>Tasks Open</th><th>Audits</th><th>Proposals</th></tr></thead><tbody>{teamPerformance.map(member=><tr key={member.name}><td>{member.name}</td><td>{member.role}</td><td>{money(member.pipeline_owned)}</td><td>{member.deals_closed}</td><td>{member.tasks_open}</td><td>{member.audits_generated}</td><td>{member.proposals_generated}</td></tr>)}</tbody></table></section></div>;
}


// 7. Security & Audit Center
// Every staff member's own password, 2FA and sessions. The routes
// (/auth/change-password, /auth/2fa/*, /security/sessions) all act on the
// caller only and are open to every role, but the only UI for 2FA and
// sessions lived in the Master-Admin-only Security & Audit Center, and
// change-password was reachable only when a reset was forced.
function AccountSecurity({ api, user, showNotice }) {
  const [tab, setTab] = useState("password");
  const tabs = [["password", "Change Password"], ["2fa", "Two-Factor Auth"], ["sessions", "My Sessions"], ["google", "Google Account"]];
  return <div className="page-stack" data-testid="account-security"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">{user?.email}</p><h2>My Account</h2></div></section><div className="button-row" data-testid="account-security-tabs">{tabs.map(([id, label]) => <button key={id} className={tab === id ? "primary-btn small" : "ghost-btn small"} data-testid={`account-security-tab-${id}`} onClick={() => setTab(id)}>{label}</button>)}</div>{tab === "password" && <ChangeOwnPassword api={api} showNotice={showNotice} />}{tab === "2fa" && <TwoFactorEnrollment api={api} user={user} showNotice={showNotice} />}{tab === "sessions" && <SessionManagement api={api} showNotice={showNotice} />}{tab === "google" && <GoogleConnectionPanel api={api} showNotice={showNotice} />}</div>;
}

function ChangeOwnPassword({ api, showNotice }) {
  const empty = { current: "", next: "", confirm: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    if (form.next !== form.confirm) {
      showNotice("New passwords do not match", "error");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      // Changing the password revokes every existing session, this one
      // included (see change_own_password) - swap in the fresh token the
      // route hands back so the tab that just submitted this form doesn't
      // itself get logged out on its next request.
      if (resp.data?.token) localStorage.setItem("msb_token", resp.data.token);
      setForm(empty);
      showNotice("Password updated", "success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      showNotice(typeof detail === "string" ? detail : "Could not update password", "error");
    } finally {
      setSaving(false);
    }
  };
  return <section className="panel glass-panel" data-testid="change-own-password"><div className="panel-header"><h3>Change Password</h3></div><form onSubmit={submit} className="login-form"><label>Current Password<input data-testid="own-pw-current" type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required /></label><label>New Password<input data-testid="own-pw-new" type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required /></label><label>Confirm New Password<input data-testid="own-pw-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required /></label><button className="primary-btn" data-testid="own-pw-submit" type="submit" disabled={saving}>{saving ? "Updating..." : "Update Password"}</button></form></section>;
}

function SecurityAuditCenter({ api, user, showNotice }) {
  const [tab, setTab] = useState("overview");
  const tabs = [
    ["overview", "Overview"],
    ["audit_log", "Audit Log"],
    ["pdf_downloads", "PDF Downloads"],
    ["sessions", "Sessions"],
    ["2fa", "Two-Factor Auth"],
  ];
  return <div className="page-stack" data-testid="security-audit-center"><section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Master Admin Only • Login History, Sessions & Two-Factor Auth</p><h2>Security & Audit Center</h2><div className="hero-stats"><span>Security Dashboard</span><span>Audit Log</span><span>Session Management</span><span>2FA Enrollment</span></div></div></section><div className="button-row" data-testid="security-audit-center-tabs">{tabs.map(([id, label]) => <button key={id} className={tab === id ? "primary-btn small" : "ghost-btn small"} data-testid={`security-audit-center-tab-${id}`} onClick={() => setTab(id)}>{label}</button>)}</div>{tab === "overview" && <SecurityDashboard api={api} user={user} showNotice={showNotice} />}{tab === "audit_log" && <AuditLogPanel api={api} showNotice={showNotice} />}{tab === "pdf_downloads" && <PDFDownloadLog api={api} showNotice={showNotice} />}{tab === "sessions" && <SessionManagement api={api} showNotice={showNotice} />}{tab === "2fa" && <TwoFactorEnrollment api={api} user={user} showNotice={showNotice} />}</div>;
}

function SkeletonDashboard() { return <div className="page-stack"><div className="skeleton hero-skeleton" /><div className="kpi-grid">{Array.from({ length: 12 }).map((_, i) => <div key={i} className="skeleton kpi-card" />)}</div></div>; }
function Loading({ label }) { return <div className="loading-panel glass-panel" data-testid="loading-panel"><Gauge className="spin" /> {label}</div>; }
function ActivityList({ items }) { return <div className="compact-list activity-list">{items.map((item) => <div key={item.id || `${item.timestamp}-${item.details}`} className="compact-row"><Activity size={16} /><div><strong>{item.actor_name || "System"} {item.action}</strong><span>{item.entity_type} • {item.details}</span></div></div>)}{!items.length && <div className="empty-cell">No activity yet.</div>}</div>; }

function AdvancedFilters({ module, filters, setFilters, team }) {
  const showRevenue = ["leads", "clients", "revenue"].includes(module);
  return <section className="advanced-filters glass-panel" data-testid={`${module}-advanced-filters`}><div className="filter-title"><Filter size={16} /><strong>Advanced Filters</strong></div><select data-testid={`${module}-filter-status`} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">Status / Stage</option>{[...PIPELINE_STAGES, "Open", "In Progress", "Done", "Active", "Onboarding", "Generated", "Delivered", "Draft", "Paid", "Pending"].map((s) => <option key={s}>{s}</option>)}</select><select data-testid={`${module}-filter-assigned-user`} value={filters.assignedUser} onChange={(e) => setFilters({ ...filters, assignedUser: e.target.value })}><option value="">Assigned User / Owner</option>{team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select><input data-testid={`${module}-filter-source`} placeholder="Lead Source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} /><input data-testid={`${module}-filter-location`} placeholder="Location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} /><input data-testid={`${module}-filter-start-date`} type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} /><input data-testid={`${module}-filter-end-date`} type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />{showRevenue && <><input data-testid={`${module}-filter-revenue-min`} type="number" placeholder="Min Revenue" value={filters.minRevenue} onChange={(e) => setFilters({ ...filters, minRevenue: e.target.value })} /><input data-testid={`${module}-filter-revenue-max`} type="number" placeholder="Max Revenue" value={filters.maxRevenue} onChange={(e) => setFilters({ ...filters, maxRevenue: e.target.value })} /></>}<button className="ghost-btn small" data-testid={`${module}-filters-reset-button`} onClick={() => setFilters(emptyFilters())}>Reset</button></section>;
}
const emptyFilters = () => ({ status: "", assignedUser: "", source: "", location: "", startDate: "", endDate: "", minRevenue: "", maxRevenue: "" });
function applyFilters(records, filters, query) { return records.filter((record) => { const haystack = JSON.stringify(record).toLowerCase(); if (query && !haystack.includes(query.toLowerCase())) return false; if (filters.status && ![record.status, record.contract_status, record.delivery_status, record.priority].includes(filters.status)) return false; if (filters.assignedUser && ![record.assigned_team_member_id, record.assignee_id, record.account_manager_id, record.owner_id, record.created_by_id].includes(filters.assignedUser)) return false; if (filters.source && !String(record.lead_source || "").toLowerCase().includes(filters.source.toLowerCase())) return false; if (filters.location && !String(record.location || "").toLowerCase().includes(filters.location.toLowerCase())) return false; const recordDate = String(record.created_at || record.start_date || record.due_date || record.date || "").slice(0, 10); if (filters.startDate && recordDate && recordDate < filters.startDate) return false; if (filters.endDate && recordDate && recordDate > filters.endDate) return false; const rev = Number(record.deal_value || record.monthly_retainer || record.retainer || 0); if (filters.minRevenue && rev < Number(filters.minRevenue)) return false; if (filters.maxRevenue && rev > Number(filters.maxRevenue)) return false; return true; }); }

function FormDrawer({ open, title, fields, value, team, onChange, onClose, onSave, saving }) { if (!open) return null; const update = (key, nextValue) => { const data = { ...value, [key]: nextValue }; const field = fields.find((f) => f[0] === key); if (field?.[2] === "team") { const member = team.find((u) => u.id === nextValue); const nameKey = key.includes("account") ? "account_manager" : key.includes("assignee") ? "assignee" : "assigned_team_member"; if (member) { data[nameKey] = member.name; data.owner_id = member.id; data.owner_name_internal = member.name; } } onChange(data); }; return <div className="drawer-backdrop" data-testid="record-drawer-backdrop"><aside className="record-drawer glass-panel"><div className="drawer-header"><h2>{title}</h2><button className="icon-button" data-testid="record-drawer-close-button" onClick={onClose}><X size={18} /></button></div><div className="form-grid">{fields.map(([key, label, type]) => <label key={key} className={type === "textarea" ? "span-2" : ""}>{label}{renderField(type, key, value?.[key], (v) => update(key, v), team)}</label>)}</div><div className="drawer-footer"><button className="ghost-btn" data-testid="record-drawer-cancel-button" onClick={onClose}>Cancel</button><button className="primary-btn" data-testid="record-drawer-save-button" onClick={onSave} disabled={saving}><Save size={16} /> {saving ? "Saving..." : "Save Record"}</button></div></aside></div>; }
function renderField(type, key, value, onChange, team) { if (type === "textarea") return <textarea data-testid={`field-${key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />; if (type === "number") return <input data-testid={`field-${key}`} type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} />; if (type === "date") return <input data-testid={`field-${key}`} type="date" value={String(value || "").slice(0, 10)} onChange={(e) => onChange(e.target.value)} />; if (type === "checkbox") return <input data-testid={`field-${key}`} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />; if (type === "team") return <select data-testid={`field-${key}`} value={value || ""} onChange={(e) => onChange(e.target.value)}><option value="">Select team member</option>{team.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}</select>; if (type === "stage") return <select data-testid={`field-${key}`} value={value || "Lead Found"} onChange={(e) => onChange(e.target.value)}>{PIPELINE_STAGES.map((s) => <option key={s}>{s}</option>)}</select>; if (type === "role") return <select data-testid={`field-${key}`} value={value || ROLES[0]} onChange={(e) => onChange(e.target.value)}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select>; if (type === "priority") return <select data-testid={`field-${key}`} value={value || "Medium"} onChange={(e) => onChange(e.target.value)}>{["Low", "Medium", "High", "Urgent"].map((r) => <option key={r}>{r}</option>)}</select>; if (type === "taskStatus") return <select data-testid={`field-${key}`} value={value || "Open"} onChange={(e) => onChange(e.target.value)}>{["Open", "In Progress", "Done", "Blocked"].map((r) => <option key={r}>{r}</option>)}</select>; return <input data-testid={`field-${key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />; }

function ClientActivationStatus({ clientId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${clientId}/activation-status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.ok ? await res.json() : null);
    } catch (err) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  const resend = async () => {
    setResending(true);
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${clientId}/resend-activation`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) await fetchStatus();
    } finally {
      setResending(false);
    }
  };
  if (loading) return <span className="muted">Loading...</span>;
  if (!status) return <span className="muted">Unknown</span>;
  const badgeClass = status.activation_status === "activated" ? "success" : status.activation_status === "expired" ? "destructive" : "warning";
  const canResend = status.activation_status === "pending" || status.activation_status === "expired";
  return <div className="client-activation-cell"><span className={`status-badge ${badgeClass}`} data-testid={`client-activation-status-${clientId}`}>{labelize(status.activation_status)}</span>{canResend && <button className="ghost-btn small" data-testid={`client-resend-activation-${clientId}-button`} onClick={resend} disabled={resending}>{resending ? "Sending..." : "Resend Activation"}</button>}</div>;
}

function TeamForceResetAction({ memberId }) {
  const [resetting, setResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  let currentUser = null;
  try { currentUser = JSON.parse(localStorage.getItem("msb_user") || "null"); } catch (err) { currentUser = null; }
  if (currentUser?.role !== "Master Administrator") return null;

  const forceReset = async () => {
    if (!window.confirm("Generate a new temporary password for this team member? They will be required to change it on next login.")) return;
    setResetting(true);
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/security/password/force-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: memberId })
      });
      if (res.ok) {
        const data = await res.json();
        setTempPassword(data.temporary_password);
      }
    } finally {
      setResetting(false);
    }
  };

  if (tempPassword) {
    return <span className="status-badge" data-testid={`team-force-reset-${memberId}-temp-password`} title="Share this with the team member - it will not be shown again">Temp: {tempPassword}</span>;
  }

  return <button className="ghost-btn small" data-testid={`team-force-reset-${memberId}-button`} onClick={forceReset} disabled={resetting}>{resetting ? "Resetting..." : "Force Reset"}</button>;
}

function SendSmsAction({ id, phone }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!phone) return null;

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/twilio/sms/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to_phone: phone, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult(data.detail || "Failed to send");
      } else if (data.success === false) {
        setResult(data.status === "suppressed" ? "Recipient has opted out" : (data.error || "Failed to send"));
      } else {
        setMessage("");
        setOpen(false);
      }
    } catch (err) {
      setResult("Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return <button className="ghost-btn small" data-testid={`send-sms-${id}-button`} onClick={() => { setOpen(true); setResult(null); }}>Text</button>;
  }

  return (
    <div className="sms-action-cell">
      <input
        data-testid={`send-sms-${id}-message-input`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={`Message to ${phone}`}
      />
      <button className="primary-btn small" data-testid={`send-sms-${id}-send-button`} onClick={send} disabled={sending || !message.trim()}>{sending ? "Sending..." : "Send"}</button>
      <button className="ghost-btn small" onClick={() => setOpen(false)}>Cancel</button>
      {result && <span className="muted">{result}</span>}
    </div>
  );
}

function SendCallAction({ id, phone }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!phone) return null;

  const call = async () => {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/twilio/call/make`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to_phone: phone, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult(data.detail || "Failed to place call");
      } else if (data.success === false) {
        setResult(data.error || "Failed to place call");
      } else {
        setMessage("");
        setOpen(false);
      }
    } catch (err) {
      setResult("Failed to place call");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return <button className="ghost-btn small" data-testid={`send-call-${id}-button`} onClick={() => { setOpen(true); setResult(null); }}>Call</button>;
  }

  return (
    <div className="sms-action-cell">
      <input
        data-testid={`send-call-${id}-message-input`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={`Script to read to ${phone}`}
      />
      <button className="primary-btn small" data-testid={`send-call-${id}-send-button`} onClick={call} disabled={sending || !message.trim()}>{sending ? "Calling..." : "Call"}</button>
      <button className="ghost-btn small" onClick={() => setOpen(false)}>Cancel</button>
      {result && <span className="muted">{result}</span>}
    </div>
  );
}

function RecordTable({ module, records, team, onCreate, onUpdate, onDelete, readonly, query }) { const schema = FIELD_SCHEMAS[module] || []; const columns = TABLE_COLUMNS[module] || []; const [drawer, setDrawer] = useState(null); const [draft, setDraft] = useState({}); const [saving, setSaving] = useState(false); const [filters, setFilters] = useState(emptyFilters()); const filtered = useMemo(() => applyFilters(records, filters, query), [records, filters, query]); const title = MODULE_TITLES[module] || labelize(module); const openNew = () => { const defaults = module === "leads" ? { status: "Lead Found", last_contact_date: today(), next_follow_up_date: today(), proposal_status: "Not Sent", follow_up_status: "Open" } : module === "tasks" ? { status: "Open", priority: "Medium", due_date: today() } : module === "team" ? { active: true, role: "Sales Representative" } : {}; setDraft(defaults); setDrawer("new"); }; const openEdit = (record) => { setDraft({ ...record }); setDrawer(record.id); }; const save = async () => { setSaving(true); try { if (drawer === "new") { const created = await onCreate(module, draft); if (module === "team" && created?.issued_temporary_password) window.prompt("Temporary password for this team member - copy it now, it will not be shown again. They must change it at first login.", created.issued_temporary_password); } else await onUpdate(module, drawer, draft); setDrawer(null); } catch { /* error notice already shown; keep drawer open */ } finally { setSaving(false); } }; const downloadPDF = async (recordId, type) => { const token = localStorage.getItem("msb_token"); try { await downloadFile(`${BACKEND_URL}/api/${type}/${recordId}/pdf`, token, `${type === "audits" ? "Audit" : "Proposal"}_${recordId.slice(0, 8)}.pdf`); } catch (err) { window.alert(err.message); } }; const downloadDocument = async (recordId, filename) => { const token = localStorage.getItem("msb_token"); try { await downloadFile(`${BACKEND_URL}/api/documents/${recordId}/download`, token, filename || `document_${recordId.slice(0, 8)}`); } catch (err) { window.alert(err.message); } }; return <div className="page-stack"><section className="module-toolbar glass-panel"><div><p className="eyebrow">Premium workspace • {filtered.length} visible records</p><h2>{title}</h2></div>{!readonly && module !== "activity" && <button className="primary-btn" data-testid={`${module}-new-record-button`} onClick={openNew}><Plus size={16} /> New {title.replace(" Management", "").replace(" Dashboard", "")}</button>}</section>{["leads", "clients", "audits", "tasks", "revenue"].includes(module) && <AdvancedFilters module={module} filters={filters} setFilters={setFilters} team={team} />}<div className="panel table-panel glass-panel"><table data-testid={`${module}-records-table`}><thead><tr>{columns.map((col) => <th key={col}>{labelize(col)}</th>)}<th>Actions</th></tr></thead><tbody>{filtered.map((record) => <tr key={record.id}>{columns.map((col) => <td key={col}>{formatCell(module === "documents" && record.grid_file_id ? (record[col] ?? (col === "title" ? record.filename : col === "owner" ? record.uploaded_by_email : undefined)) : record[col], col)}</td>)}<td className="table-actions">{module === "audits" && <button className="primary-btn small" data-testid={`audit-pdf-${record.id}-button`} onClick={() => downloadPDF(record.id, "audits")}><FileDown size={14} /> PDF</button>}{module === "documents" && record.grid_file_id && <button className="primary-btn small" data-testid={`document-download-${record.id}-button`} onClick={() => downloadDocument(record.id, record.filename)}><FileDown size={14} /> Download</button>}{module === "proposal_generator" && <><button className="primary-btn small" data-testid={`proposal-pdf-${record.id}-button`} onClick={() => downloadPDF(record.id, "proposals")}><FileDown size={14} /> PDF</button><ProposalSendAction proposal={record} /></>}{module === "clients" && <ClientActivationStatus clientId={record.id} />}{module === "team" && <TeamForceResetAction memberId={record.id} />}{(module === "leads" || module === "clients") && <SendSmsAction id={record.id} phone={record.phone} />}{(module === "leads" || module === "clients") && <SendCallAction id={record.id} phone={record.phone} />}{module !== "activity" && <button className="ghost-btn small" data-testid={`${module}-edit-${record.id}-button`} onClick={() => openEdit(record)}>Edit</button>}{!readonly && module !== "activity" && <button className="danger-btn small" data-testid={`${module}-delete-${record.id}-button`} onClick={() => onDelete(module, record.id)}>Delete</button>}</td></tr>)}{!filtered.length && <tr><td colSpan={columns.length + 1} className="empty-cell">No visible records match this role, search, or filters.</td></tr>}</tbody></table></div><FormDrawer open={Boolean(drawer)} title={drawer === "new" ? `New ${title}` : `Edit ${title}`} fields={module === "team" && drawer !== "new" ? schema.filter(([key]) => key !== "password") : schema} value={draft} team={team} onChange={setDraft} onClose={() => setDrawer(null)} onSave={save} saving={saving} /></div>; }
function formatCell(value, col) { if (value === undefined || value === null || value === "") return <span className="muted">—</span>; if (["deal_value", "setup_fee", "monthly_retainer", "ad_spend", "retainer", "upsells", "proposal_value"].includes(col)) return money(value); if (col === "active") return value ? "Active" : "Inactive"; if (String(col).includes("status") || col === "priority" || col === "role") return <span className="status-badge">{String(value)}</span>; if (String(value).startsWith("http")) return <a href={value} target="_blank" rel="noreferrer">Open link</a>; return String(value).length > 90 ? `${String(value).slice(0, 90)}...` : String(value); }

function Pipeline({ leads, onMove }) { const [dragged, setDragged] = useState(null); const grouped = PIPELINE_STAGES.reduce((acc, stage) => ({ ...acc, [stage]: leads.filter((lead) => lead.status === stage) }), {}); return <div className="page-stack"><section className="module-toolbar glass-panel"><div><p className="eyebrow">Drag/drop pipeline with assignment fallback</p><h2>Sales Pipeline</h2></div></section><section className="pipeline-board" data-testid="sales-pipeline-board">{PIPELINE_STAGES.map((stage) => <div key={stage} className="pipeline-column glass-panel" data-testid={`pipeline-stage-${slug(stage)}-column`} onDragOver={(e) => e.preventDefault()} onDrop={() => dragged && onMove(dragged, stage)}><div className="pipeline-column-header"><strong>{stage}</strong><span>{money((grouped[stage] || []).reduce((sum, lead) => sum + Number(lead.deal_value || 0), 0))}</span></div>{(grouped[stage] || []).map((lead) => <article key={lead.id} draggable className="pipeline-card" data-testid={`pipeline-card-${lead.id}`} onDragStart={() => setDragged(lead.id)}><strong>{lead.clinic_name}</strong><span>{lead.assigned_team_member || "Unassigned"} • {lead.location}</span><b>{money(lead.deal_value)}</b><select data-testid={`pipeline-card-${lead.id}-stage-select`} value={lead.status} onChange={(e) => onMove(lead.id, e.target.value)}>{PIPELINE_STAGES.map((s) => <option key={s}>{s}</option>)}</select></article>)}</div>)}</section></div>; }
function AuditCenter({ leads, audits, onGenerate, onCreate, onUpdate, onDelete, generating }) { const [form, setForm] = useState({ lead_id: "", clinic_name: "", website_url: "", google_business_url: "", instagram_url: "" }); const selectLead = (id) => { const lead = leads.find((item) => item.id === id); setForm({ lead_id: id, clinic_name: lead?.clinic_name || "", website_url: lead?.website || "", google_business_url: lead?.google_business_profile || "", instagram_url: lead?.instagram || "" }); }; const latest = audits[0]; return <div className="audit-layout"><section className="panel audit-form-panel glass-panel"><div className="panel-header"><h3>AI Audit Engine</h3><span>Premium growth audit generator</span></div><label>Attach to lead<select data-testid="audit-engine-lead-select" value={form.lead_id} onChange={(e) => selectLead(e.target.value)}><option value="">Standalone audit</option>{leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.clinic_name}</option>)}</select></label><label>Clinic Name<input data-testid="audit-engine-clinic-name-input" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} /></label><label>Website URL<input data-testid="audit-engine-website-url-input" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} /></label><label>Google Business URL<input data-testid="audit-engine-google-url-input" value={form.google_business_url} onChange={(e) => setForm({ ...form, google_business_url: e.target.value })} /></label><label>Instagram URL<input data-testid="audit-engine-instagram-url-input" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} /></label><button className="primary-btn" data-testid="audit-engine-generate-button" disabled={generating || !form.clinic_name || !form.website_url} onClick={() => onGenerate(form)}><Sparkles size={16} /> {generating ? "Generating Audit..." : "Generate & Store Audit"}</button></section><section className="panel audit-preview-panel glass-panel" data-testid="audit-engine-preview-panel"><div className="panel-header"><h3>Latest Audit Output</h3><span>Scores + action plan</span></div>{latest ? <AuditPreview audit={latest} /> : <div className="empty-cell">Generate the first audit to see scorecards here.</div>}</section><section className="audit-table-wrap"><RecordTable module="audits" records={audits} team={[]} onCreate={onCreate} onUpdate={onUpdate} onDelete={onDelete} readonly={false} query="" /></section></div>; }
function AuditPreview({ audit }) { const scores = ["website_conversion_score", "google_reputation_score", "social_media_score", "lead_capture_followup_score", "revenue_opportunity_score", "overall_medspa_growth_score"]; return <div className="audit-preview"><h2>{audit.clinic_name}</h2><div className="score-grid">{scores.map((score) => <div key={score} className="score-card"><span>{labelize(score)}</span><strong>{audit[score] ?? "—"}</strong></div>)}</div><p>{audit.executive_summary}</p><h4>Problems Found</h4><ul>{(audit.problems_found || []).map((item, i) => <li key={i}>{item}</li>)}</ul><h4>Growth Opportunities</h4><ul>{(audit.growth_opportunities || []).map((item, i) => <li key={i}>{item}</li>)}</ul><h4>Revenue Opportunity</h4><p>{audit.revenue_opportunity_estimate}</p></div>; }
function Permissions({ permissions, modules, onSave }) { const [draft, setDraft] = useState(permissions || {}); const toggle = (role, module) => { if (role === "Master Administrator") return; const current = draft[role] || []; const next = current.includes(module) ? current.filter((m) => m !== module) : [...current, module]; setDraft({ ...draft, [role]: next }); }; return <div className="page-stack"><section className="module-toolbar glass-panel"><div><p className="eyebrow">Permission architecture</p><h2>Role Access Matrix</h2></div></section><div className="panel permission-matrix glass-panel"><table data-testid="permissions-matrix-table"><thead><tr><th>Role</th>{modules.map((m) => <th key={m}>{MODULE_TITLES[m] || m}</th>)}<th>Save</th></tr></thead><tbody>{ROLES.map((role) => <tr key={role}><td><strong>{role}</strong></td>{modules.map((m) => <td key={m}><button data-testid={`permission-toggle-${slug(role)}-${m}`} className={`toggle ${draft[role]?.includes(m) ? "on" : ""}`} disabled={role === "Master Administrator"} onClick={() => toggle(role, m)}>{draft[role]?.includes(m) ? "On" : "Off"}</button></td>)}<td><button className="ghost-btn small" data-testid={`permission-save-${slug(role)}-button`} disabled={role === "Master Administrator"} onClick={() => onSave(role, draft[role] || [])}>Save</button></td></tr>)}</tbody></table></div></div>; }

/* eslint-disable react-hooks/rules-of-hooks */
function App() {
  // Public, unauthenticated self-serve booking widget: /book/{clientId}.
  // Checked before every other route since it has no app shell, no auth,
  // and needs none of the state below - a patient with this link should
  // never touch team/client-portal login state.
  const bookingMatch = window.location.pathname.match(/^\/book\/([^/]+)/);
  if (bookingMatch) return <PublicBookingWidget clientId={bookingMatch[1]} />;

  // Public, unauthenticated proposal page: /proposal/{token}, from the
  // private link emailed when staff send a proposal. Same reasoning as the
  // booking widget - no app shell, no login.
  const proposalMatch = window.location.pathname.match(/^\/proposal\/([^/]+)/);
  if (proposalMatch) {
    const PublicProposalPage = require("@/PublicProposalPage").default;
    return <PublicProposalPage token={decodeURIComponent(proposalMatch[1])} />;
  }

  // Public, unauthenticated legal pages: /terms and /privacy. Same reasoning
  // as the booking widget above - readable before any login, no app shell.
  if (window.location.pathname === "/terms") return <TermsOfService />;
  if (window.location.pathname === "/privacy") return <PrivacyPolicy />;

  // Public, unauthenticated unsubscribe landing page, reached from an
  // outbound email's unsubscribe link. Same reasoning as above.
  if (window.location.pathname === "/unsubscribe") {
    const UnsubscribePage = require("@/UnsubscribePage").default;
    return <UnsubscribePage />;
  }

  // PILLAR 1: Check if accessing client portal
  const isClientPortal = window.location.pathname.startsWith("/client") || window.location.hash.startsWith("#/client");

  // Phase 6: Check for client activation route
  if (window.location.pathname === "/client/activate" || window.location.hash === "#/client/activate") {
    const ClientActivation = require("@/ClientActivation").default;
    return <ClientActivation />;
  }

  // Client self-service password reset, reached from the emailed reset link.
  if (window.location.pathname === "/client/reset-password" || window.location.hash === "#/client/reset-password") {
    const ClientResetPassword = require("@/ClientResetPassword").default;
    return <ClientResetPassword />;
  }

  // Staff self-service password reset (team login "Forgot password?" and
  // the emailed link built by send_staff_password_reset_email).
  if (window.location.pathname === "/forgot-password" || window.location.pathname === "/reset-password") {
    const { StaffForgotPassword, StaffResetPassword } = require("@/StaffPasswordReset");
    return window.location.pathname === "/forgot-password" ? <StaffForgotPassword /> : <StaffResetPassword />;
  }

  if (isClientPortal) return <ClientPortal />;
  
  // Team portal (all hooks after client portal check)
  // Rehydrate from localStorage on mount (mirrors ClientPortal.js) so a
  // page refresh doesn't force re-login. A stale/expired token is caught
  // by refresh()'s existing 401 handler below, which calls logout().
  const [token, setToken] = useState(() => localStorage.getItem("msb_token") || "");
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("msb_user"));
    } catch {
      return null;
    }
  });
  const [permissions, setPermissions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("msb_permissions")) || [];
    } catch {
      return [];
    }
  });
  const [active, setActive] = useState("dashboard");
  const [metadata, setMetadata] = useState({ team: [], modules: [], role_permissions: {} });
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [records, setRecords] = useState({});
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Phase 9 Revenue Engine State
  const [revenueEngineView, setRevenueEngineView] = useState('discovery');
  const [selectedLead, setSelectedLead] = useState(null);

  const showNotice = (message, type = "info") => setNotice({ message, type });

  const onLogin = ({ token: nextToken, user: nextUser, permissions: nextPermissions }) => {
    localStorage.setItem("msb_token", nextToken);
    localStorage.setItem("msb_user", JSON.stringify(nextUser));
    localStorage.setItem("msb_permissions", JSON.stringify(nextPermissions));
    setToken(nextToken);
    setUser(nextUser);
    setPermissions(nextPermissions);
    setActive(nextPermissions.includes("dashboard") ? "dashboard" : (nextPermissions[0] || "dashboard"));
  };

  const logout = () => {
    localStorage.clear();
    setToken("");
    setUser(null);
    setPermissions([]);
    setAnalytics(null);
  };

  const api = useMemo(() => axios.create({ baseURL: API, headers: token ? authHeaders(token) : {} }), [token]);

  const refresh = useCallback(async (module = active) => {
    if (!token) return;
    setLoading(true);
    try {
      const meta = await api.get("/metadata");
      setMetadata(meta.data);
      const currentPermissions = meta.data.current_permissions || [];
      setPermissions(currentPermissions);
      if (currentPermissions.length && !isModuleAllowed(module, NAV, currentPermissions, user)) {
        setActive(currentPermissions[0]);
      }
      if (currentPermissions.includes("dashboard")) {
        const dash = await api.get("/dashboard");
        setDashboard(dash.data);
        const analyticsRes = await api.get("/analytics");
        setAnalytics(analyticsRes.data);
      }
      if (currentPermissions.includes("lead_acquisition")) {
        const acquisition = await api.get("/lead-acquisition/dashboard");
        setRecords((prev) => ({ ...prev, lead_acquisition_dashboard: acquisition.data }));
      }
      if (currentPermissions.includes("onboarding_center")) {
        const onboarding = await api.get("/onboarding/dashboard");
        setRecords((prev) => ({ ...prev, onboarding_dashboard: onboarding.data }));
      }
      if (currentPermissions.includes("inbox")) {
        const inbox = await api.get("/inbox/dashboard");
        setRecords((prev) => ({ ...prev, inbox_dashboard: inbox.data, inbox: inbox.data.messages || [] }));
      }
      if (currentPermissions.includes("integrations")) {
        const integrationCenter = await api.get("/integration-center/dashboard");
        setRecords((prev) => ({ ...prev, integration_center: integrationCenter.data }));
      }
      // Phase 7: Load operational centers for Master Admin
      if (user?.role === "Master Administrator") {
        if (module === "system_health") {
          const health = await api.get("/system/health");
          setRecords((prev) => ({ ...prev, system_health: health.data }));
        }
      }
      const targets = new Set([module, "leads", "audits", "tasks", "clients", ...PHASE3_COLLECTIONS]);
      if (module === "dashboard") {
        ["leads", "tasks", "clients", "revenue", "activities"].forEach((item) => targets.add(item));
      }
      for (const mod of [...targets]) {
        // Modules that load their own data (or render from another module's
        // records, e.g. pipeline -> leads) must be skipped here: GET /api/<mod>
        // for a non-collection 404s "Unknown collection", which aborts the rest
        // of this refresh. backend/tests/test_frontend_refresh_collections.py
        // guards this list against the backend's COLLECTION_MODULE.
        if (mod === "permissions" || mod === "lead_acquisition" || mod === "revenue_engine" || mod === "revenue_dashboard" || mod === "priority_queue" || mod === "compliance_center" || mod === "outreach_approval" || mod === "automation_approvals" || mod === "provider_management" || mod === "lead_enrichment" || mod === "system_health" || mod === "demo_workspace" || mod === "qa_testing" || mod === "backups" || mod === "notifications" || mod === "automations" || mod === "security" || mod === "client_success" || mod === "pipeline" || mod === "billing" || mod === "admin_analytics") continue;
        const collection = COLLECTION_BY_MODULE[mod] || mod;
        const moduleId = mod === "appointments" ? "calendar" : mod === "activities" ? "activity" : mod;
        if (currentPermissions.includes(moduleId)) {
          const res = await api.get(`/${collection}`);
          setRecords((prev) => ({ ...prev, [moduleId]: res.data }));
        }
      }
    } catch (err) {
      if (err.response?.status === 401) logout();
      else showNotice(err.response?.data?.detail || "Unable to refresh data", "error");
    } finally {
      setLoading(false);
    }
  }, [active, api, token, user]);

  useEffect(() => {
    if (token) window.setTimeout(() => refresh(active), 0);
  }, [token, active, refresh]);

  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      api.get("/search", { params: { q: query } })
        .then((res) => setSearchResults(res.data.results || []))
        .catch(() => setSearchResults([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, api]);

  // Show the save failure, then rethrow so callers (e.g. the form drawer)
  // know it failed and stay open.
  const reportSaveError = (err) => {
    const detail = err.response?.data?.detail;
    showNotice(err.response?.status === 422 ? "Please fill in at least one field before saving" : (typeof detail === "string" && detail) || "Unable to save record", "error");
    throw err;
  };

  const createRecord = async (module, data) => {
    const collection = COLLECTION_BY_MODULE[module] || module;
    const res = await api.post(`/${collection}`, { data }).catch(reportSaveError);
    showNotice("Record created", "success");
    await refresh(module);
    return res?.data;
  };

  const updateRecord = async (module, id, data) => {
    const collection = COLLECTION_BY_MODULE[module] || module;
    await api.put(`/${collection}/${id}`, { data }).catch(reportSaveError);
    showNotice("Record updated", "success");
    await refresh(module);
  };

  const deleteRecord = async (module, id) => {
    const confirmed = window.confirm("Delete this internal record?");
    if (!confirmed) return;
    const collection = COLLECTION_BY_MODULE[module] || module;
    await api.delete(`/${collection}/${id}`);
    showNotice("Record deleted", "success");
    await refresh(module);
  };

  const moveLead = async (id, stage) => {
    const lead = (records.leads || []).find((item) => item.id === id);
    if (lead) await updateRecord("leads", id, { ...lead, status: stage });
  };

  const generateAudit = async (payload) => {
    setGenerating(true);
    try {
      await api.post("/audits/generate", payload);
      showNotice("AI audit generated and stored in clinic record", "success");
      await refresh("audits");
    } catch (err) {
      showNotice(err.response?.data?.detail || "AI audit generation failed", "error");
    } finally {
      setGenerating(false);
    }
  };

  const savePermissions = async (role, modules) => {
    await api.put(`/permissions/${role}`, { data: { modules } });
    showNotice(`Permissions updated for ${role}`, "success");
    await refresh("permissions");
  };


  // ========================================
  // PHASE 4: PUBLIC PAYMENT PAGES (NO AUTH REQUIRED)
  // ========================================
  
  // Public Payment Success Page Component
  const PaymentSuccessPage = ({ sessionId }) => {
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    useEffect(() => {
      const fetchPaymentStatus = async () => {
        if (!sessionId) {
          setError("No payment session found");
          setLoading(false);
          return;
        }
        
        try {
          const res = await axios.get(`${API}/stripe/checkout-status-public?session_id=${sessionId}`);
          setPaymentData(res.data);
        } catch (err) {
          setError("Failed to load payment details. Please contact support.");
        } finally {
          setLoading(false);
        }
      };
      
      fetchPaymentStatus();
    }, [sessionId]);
    
    if (loading) {
      return (
        <div className="public-payment-page" data-testid="payment-success-page">
          <div className="payment-card glass-panel">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading payment details...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error || !paymentData) {
      return (
        <div className="public-payment-page" data-testid="payment-success-page">
          <div className="payment-card glass-panel">
            <div className="payment-icon error">❌</div>
            <h1>Payment Status Unavailable</h1>
            <p className="error-message">{error || "Unable to load payment information"}</p>
            <p className="support-text">
              If you completed a payment, please contact us at{" "}
              <a href="mailto:support@medspabookings.co">support@medspabookings.co</a>
            </p>
            <button className="primary-btn" onClick={() => window.location.href = "/"}>
              Return to Home
            </button>
          </div>
        </div>
      );
    }
    
    const isPaid = paymentData.status === "success" || paymentData.payment_status === "paid";
    const isSubscription = paymentData.package_id === "monthly_management" || paymentData.mode === "subscription";
    
    return (
      <div className="public-payment-page" data-testid="payment-success-page">
        <div className="payment-card glass-panel">
          <div className={`payment-icon ${isPaid ? "success" : "pending"}`}>
            {isPaid ? (isSubscription ? "✅ 🔄" : "✅") : "⏳"}
          </div>
          
          <h1>
            {isPaid 
              ? (isSubscription ? "Subscription Active!" : "Payment Successful!") 
              : "Payment Processing"}
          </h1>
          
          <div className="payment-details">
            <div className="detail-row">
              <span className="detail-label">Clinic:</span>
              <span className="detail-value">{paymentData.clinic_name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Package:</span>
              <span className="detail-value">{paymentData.package_name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Amount:</span>
              <span className="detail-value">
                {isSubscription 
                  ? `$${(paymentData.amount || 0).toLocaleString()}/month ${paymentData.currency?.toUpperCase()}`
                  : (paymentData.amount_display || `$${(paymentData.amount || 0).toLocaleString()} ${paymentData.currency}`)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{paymentData.client_email_masked}</span>
            </div>
            {isPaid && !isSubscription && (
              <div className="detail-row">
                <span className="detail-label">Onboarding:</span>
                <span className="detail-value">{paymentData.onboarding_status}</span>
              </div>
            )}
            {isPaid && isSubscription && paymentData.next_billing_date && (
              <div className="detail-row">
                <span className="detail-label">Next Billing:</span>
                <span className="detail-value">{new Date(paymentData.next_billing_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          
          <div className="next-steps-section">
            <h3>Next Steps</h3>
            {isSubscription ? (
              <p>
                Your monthly subscription is now active. You&apos;ll be automatically billed ${paymentData.amount || 800}/month. 
                {paymentData.next_steps ? ` ${paymentData.next_steps}` : ' Our team will reach out within 24 hours to begin onboarding.'}
              </p>
            ) : (
              <p>{paymentData.next_steps}</p>
            )}
          </div>
          
          <div className="action-buttons">
            <button 
              className="primary-btn" 
              onClick={() => window.location.href = "/client"}
              data-testid="goto-login-button"
            >
              Go to Client Portal Login
            </button>
          </div>
          
          <div className="support-section">
            <p className="support-text">
              {paymentData.support_message}{" "}
              <a href={`mailto:${paymentData.support_email}`}>{paymentData.support_email}</a>
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  // Public Payment Cancel Page Component
  const PaymentCancelPage = ({ sessionId }) => {
    return (
      <div className="public-payment-page" data-testid="payment-cancel-page">
        <div className="payment-card glass-panel">
          <div className="payment-icon warning">⚠️</div>
          
          <h1>Payment Not Completed</h1>
          
          <p className="cancel-message">
            Your payment was not completed. No charges have been made to your card.
          </p>
          
          <div className="cancel-info">
            <h3>What Happened?</h3>
            <p>
              You may have clicked &quot;Back&quot; in the payment window, or the session may have expired. 
              Your clinic onboarding has not started yet.
            </p>
          </div>
          
          <div className="action-buttons">
            <button 
              className="primary-btn" 
              onClick={() => window.location.href = "/"}
              data-testid="return-home-button"
            >
              Return to Home
            </button>
          </div>
          
          <div className="support-section">
            <h3>Need Help?</h3>
            <p className="support-text">
              If you need assistance or want to complete your payment, please contact us:
            </p>
            <p className="support-email">
              <a href="mailto:support@medspabookings.co">support@medspabookings.co</a>
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  // Phase 4: Public route detection - BEFORE auth guard
  const currentPath = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  
  if (currentPath === '/payment-success') {
    return <PaymentSuccessPage sessionId={urlParams.get('session_id')} />;
  }
  
  if (currentPath === '/payment-cancel') {
    return <PaymentCancelPage sessionId={urlParams.get('session_id')} />;
  }
  
  // End Phase 4 Public Routes
  // ========================================


  if (!token || !user) return <Login onLogin={onLogin} />;

  if (user.force_password_reset || user.requires_password_reset) {
    return (
      <ForcePasswordChange
        token={token}
        user={user}
        onChanged={() => {
          const updatedUser = { ...user, force_password_reset: false, requires_password_reset: false };
          localStorage.setItem("msb_user", JSON.stringify(updatedUser));
          setUser(updatedUser);
          showNotice("Password updated.", "success");
        }}
      />
    );
  }

  const renderModule = () => {
    if (loading && !dashboard && active === "dashboard") return <Loading label="Loading executive command center" />;
    if (active === "dashboard") return <Dashboard dashboard={dashboard} analytics={analytics} setActive={setActive} user={user} />;
    if (active === "pipeline") return <Pipeline leads={records.leads || []} onMove={moveLead} />;
    if (active === "audits") return <AuditCenter leads={records.leads || []} audits={records.audits || []} onGenerate={generateAudit} onCreate={createRecord} onUpdate={updateRecord} onDelete={deleteRecord} generating={generating} />;
    if (active === "permissions") return <Permissions permissions={metadata.role_permissions} modules={metadata.modules} onSave={savePermissions} />;
    if (active === "lead_acquisition") return <LeadAcquisitionCenter data={records.lead_acquisition_dashboard} team={metadata.team || []} api={api} refresh={refresh} showNotice={showNotice} user={user} />;
    if (active === "ai_sales") return <AISalesCenter api={api} refresh={refresh} showNotice={showNotice} records={records} onUpdate={updateRecord} />;
    if (active === "onboarding_center") return <OnboardingCenter data={records.onboarding_dashboard} api={api} refresh={refresh} showNotice={showNotice} />;
    if (active === "integrations") return <IntegrationCenter data={records.integration_center} api={api} refresh={refresh} showNotice={showNotice} user={user} />;
    
    // Phase 9E-9H: Revenue Dashboard & Priority Queue
    if (active === "revenue_dashboard") return <RevenueDashboard user={{...user, token}} />;
    if (active === "priority_queue") return <AIPriorityQueue user={{...user, token}} onNavigate={(view, lead) => {
      if (view === 'detail') {
        setActive('revenue_engine');
        setRevenueEngineView('detail');
        if (lead) setSelectedLead(lead);
      }
    }} />;
    
    // Phase 9: Revenue Engine (Lead Discovery, Scoring, Intelligence)
    if (active === "revenue_engine") {
      const handleNavigate = (view, lead = null) => {
        setRevenueEngineView(view);
        if (lead) setSelectedLead(lead);
      };
      
      if (revenueEngineView === 'discovery') {
        return <LeadDiscoveryDashboard user={{...user, token}} onNavigate={handleNavigate} />;
      }
      if (revenueEngineView === 'database') {
        return <LeadDatabase user={{...user, token}} onNavigate={handleNavigate} />;
      }
      if (revenueEngineView === 'detail') {
        return <LeadCommandCenter user={{...user, token}} onNavigate={handleNavigate} selectedLead={selectedLead} />;
      }
      if (revenueEngineView === 'import') {
        return <CSVImportWizard user={{...user, token}} onNavigate={handleNavigate} />;
      }
      if (revenueEngineView === 'duplicates') {
        return <DuplicateReviewQueue user={{...user, token}} onNavigate={handleNavigate} />;
      }
      if (revenueEngineView === 'scoring') {
        return <AILeadScoringDashboard user={{...user, token}} onNavigate={handleNavigate} />;
      }
      if (revenueEngineView === 'intelligence') {
        return <AILeadIntelligenceDashboard user={{...user, token}} />;
      }
      // Default to discovery
      return <LeadDiscoveryDashboard user={{...user, token}} onNavigate={handleNavigate} />;
    }
    
    // Phase 9E-9H: Revenue Engine Additional Pages
    if (active === "revenue_dashboard") return <RevenueDashboard user={{...user, token}} />;
    if (active === "priority_queue") {
      const handleNavigate = (view, lead = null) => {
        setActive("revenue_engine");
        setRevenueEngineView(view);
        if (lead) setSelectedLead(lead);
      };
      return <AIPriorityQueue user={{...user, token}} onNavigate={handleNavigate} />;
    }
    if (active === "compliance_center") return <ComplianceCenter user={{...user, token}} />;
    if (active === "outreach_approval") return <OutreachApprovalQueue user={{...user, token}} />;
    if (active === "provider_management") return <ProviderManagement user={{...user, token}} />;
    if (active === "lead_enrichment") return <LeadEnrichmentManagement user={{...user, token}} />;
    if (active === "calendar") return <BookingCenter user={{...user, token}} />;
    
    // Phase 7: Production Readiness Centers (Master Admin Only)
    if (active === "system_health") return <SystemHealthDashboard user={{...user, token}} />;
    if (active === "demo_workspace") return <DemoWorkspaceManager user={{...user, token}} />;
    if (active === "backups") return <BackupRecoveryCenter api={api} showNotice={showNotice} />;
    if (active === "notifications") return <NotificationCenterPage api={api} showNotice={showNotice} />;
    if (active === "automations") return <AutomationCenter api={api} showNotice={showNotice} user={user} />;
    if (active === "automation_approvals") return <TemplateApprovals api={api} showNotice={showNotice} user={user} />;
    if (active === "qa_testing") return <QATestingCenter api={api} showNotice={showNotice} />;
    if (active === "admin_analytics") return <MasterAdminAnalyticsCenter api={api} showNotice={showNotice} />;
    if (active === "security") return <SecurityAuditCenter api={api} user={user} showNotice={showNotice} />;
    if (active === "account") return <AccountSecurity api={api} user={user} showNotice={showNotice} />;
    if (active === "client_success") return <ClientSuccessCenter user={{...user, token}} onNavigate={setActive} />;
    // Stripe Billing Center (Master Admin Only)
    if (active === "billing") return <StripeBillingCenter api={api} showNotice={showNotice} />;
    if (active === "inbox") return <UnifiedInbox api={api} team={metadata.team || []} user={user} showNotice={showNotice} />;
    if (active === "cold_email") return <ColdEmailSequences user={{...user, token}} />;
    if (["cold_calling", "proposal_generator", "inbox"].includes(active)) return <RevenueModule module={active} records={records[active] || []} team={metadata.team || []} onCreate={createRecord} onUpdate={updateRecord} onDelete={deleteRecord} query={query} />;
    return <RecordTable module={active} records={records[active] || []} team={metadata.team || []} onCreate={createRecord} onUpdate={updateRecord} onDelete={deleteRecord} readonly={active === "activity"} query={query} />;
  };

  return (
    <>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <AppShell user={user} permissions={permissions} active={active} setActive={setActive} onLogout={logout} query={query} setQuery={setQuery} searchResults={searchResults} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} collapsed={collapsed} setCollapsed={setCollapsed}>
        {renderModule()}
        <div className="build-marker" data-testid="build-version-marker">Build: 2026-06-21-LAUNCH-P4.1</div>
      </AppShell>
    </>
  );
}
// ErrorBoundary is defined above (a complete, working fallback UI) but was
// never mounted anywhere in the tree - any render error anywhere in App
// (including the client-portal/activation branches App renders directly,
// before its own hooks run) white-screened with no fallback. Wrapping the
// export, rather than restructuring App's many internal early returns, is
// the smallest change that covers all of them.
function AppWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <App {...props} />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
